import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { PermissionGuard } from '../components/common/PermissionGuard';
import citiesData from '../data/cities_with_coordinates.json';
import ContactsHeatMap from '../components/common/ContactsHeatMap';
import ImportContactsModal from '../components/common/ImportContactsModal';
import TagSelector, { Tag, getTagColor } from '../components/common/TagSelector';
import NewContactModal from '../components/common/NewContactModal';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';
import ContactSyncCard from '../components/common/ContactSyncCard';
import ContactDetailModalMobile from '../components/common/ContactDetailModalMobile';

interface CityData {
  id: number;
  nome: string;
  microrregiao: {
    mesorregiao: {
      UF: {
        sigla: string;
      };
    };
  };
}

interface Eleitor {
  id: number;
  nome: string;
  telefone: string | null;
  email: string | null;
  cidade_id: number | null;
  data_nascimento: string | null;
  cidade_nascimento: string | null;
  profissao: string | null;
  time_coracao: string | null;
  rede_social: string | null;
  observacoes: string | null;
  multiplicador?: boolean;
  created_at?: string;
  updated_at?: string;
  data_cadastro?: string;
  cidade?: {
    id: number;
    nome: string;
    uf: string;
  } | null;
  assessor?: {
    id: number;
    nome: string;
  } | null;
  tags?: Tag[];
}

interface Registro {
  id: number;
  descricao: string;
  created_at: string;
  updated_at: string;
  usuario: {
    id: number;
    nome: string;
  };
}

interface LogEntry {
  id: number;
  data_hora: string;
  acao: string;
  observacao: string;
  registrou: { id: number; nome: string; } | null;
}

const MobileSearchRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    width: 100%;
    box-sizing: border-box;
  }
`;

const Contacts: React.FC<{
  displayMode?: 'map' | 'list';
  onDisplayModeChange?: (mode: 'map' | 'list') => void;
}> = ({ displayMode: displayModeProp, onDisplayModeChange }) => {
  const { session, userData } = useAuth();
  const navigate = useNavigate();
  const { can, loading: permissionsLoading } = usePermissionCheck();
  const [displayModeInternal, setDisplayModeInternal] = React.useState<'map' | 'list'>(() => {
    const saved = localStorage.getItem('displayMode');
    return (saved === 'map' || saved === 'list') ? saved : 'list';
  });
  const displayMode = displayModeProp ?? displayModeInternal;
  const viewMode = displayMode === 'map' ? 'map' : 'table';
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState<Eleitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [filterTagIds, setFilterTagIds] = useState<number[]>([]);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [allGroupTags, setAllGroupTags] = useState<Tag[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Eleitor | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Can edit if: edit.all, OR edit.own and this contact was assigned to the current user
  const canEditContact =
    !permissionsLoading && (
      can('contacts.edit.all') ||
      (can('contacts.edit.own') && !!selectedContact && selectedContact.assessor?.id === userData?.id)
    );

  // Can delete if: delete.all (any contact) OR delete.own (only contacts assigned to the current user)
  const canDeleteContact =
    !permissionsLoading && (
      can('contacts.delete.all') ||
      (can('contacts.delete.own') && !!selectedContact && selectedContact.assessor?.id === userData?.id)
    );
  const [visibleCount, setVisibleCount] = useState(40);
  const [loadingMore, setLoadingMore] = useState(false);
  const infiniteScrollSentinelRef = useRef<HTMLDivElement>(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [filterBirthdays, setFilterBirthdays] = useState(false);
  const [filterExpiring, setFilterExpiring] = useState(false);
  const [birthdayBannerDismissed, setBirthdayBannerDismissed] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [filterProfissao, setFilterProfissao] = useState('');
  const [filterOpenDemands, setFilterOpenDemands] = useState(false);
  const [filterMultiplicadores, setFilterMultiplicadores] = useState(false);
  const [filterAssessorId, setFilterAssessorId] = useState<number | null>(null);
  const [isAssessorDropdownOpen, setIsAssessorDropdownOpen] = useState(false);
  const [assessorSearchTerm, setAssessorSearchTerm] = useState('');
  const [totalOpenDemandsCount, setTotalOpenDemandsCount] = useState(0);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [contactsWithOpenDemands, setContactsWithOpenDemands] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'registros' | 'demandas'>('info');
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [loadingRegistros, setLoadingRegistros] = useState(false);
  const [demandas, setDemandas] = useState<any[]>([]);
  const [loadingDemandas, setLoadingDemandas] = useState(false);
  const [newRegistro, setNewRegistro] = useState('');
  const [editingRegistro, setEditingRegistro] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [addingRegistro, setAddingRegistro] = useState(false);
  const [savingRegistroId, setSavingRegistroId] = useState<number | null>(null);
  const [deletingRegistroId, setDeletingRegistroId] = useState<number | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [registrosFilter, setRegistrosFilter] = useState<'tudo' | 'comentarios' | 'historico'>('tudo');
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const selectedContactRef = useRef<Eleitor | null>(null);
  selectedContactRef.current = selectedContact;
  const [mobileDetailId, setMobileDetailId] = useState<number | null>(null);
  const [visibleMax, setVisibleMax] = useState(1);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    cidade_id: '',
    data_nascimento: '',
    profissao: '',
    time_coracao: '',
    rede_social: '',
    observacoes: '',
    multiplicador: false,
  });

  const cities = (citiesData as CityData[]).filter(city => city.microrregiao?.mesorregiao?.UF?.sigla === 'RS');
  const sortedCities = [...cities].sort((a, b) => a.nome.localeCompare(b.nome));

  const timesRS = [
    'Grêmio',
    'Internacional',
    'Juventude',
    'Caxias',
    'Brasil de Pelotas',
    'Ypiranga',
    'São José (POA)',
    'Esportivo',
    'Aimoré',
    'São Luiz',
    'Guarany de Bagé',
    'Outros',
  ];

  useEffect(() => {
    if (permissionsLoading) return;
    fetchContacts();
    fetchContactsWithOpenDemands();
    fetchGroupTags();
  }, [session, permissionsLoading]);

  const fetchGroupTags = async () => {
    if (!session?.access_token) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/tags`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setAllGroupTags(d.data || []);
      }
    } catch {}
  };

  // Close city dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-city-select]')) {
        setIsCityDropdownOpen(false);
      }
    };

    if (isCityDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isCityDropdownOpen]);

  // Close tag dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-tag-select]')) {
        setIsTagDropdownOpen(false);
      }
    };

    if (isTagDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isTagDropdownOpen]);

  // Close assessor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-assessor-select]')) {
        setIsAssessorDropdownOpen(false);
      }
    };

    if (isAssessorDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isAssessorDropdownOpen]);

  const fetchContactsWithOpenDemands = async () => {
    if (!session?.access_token) return;
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas?status=NAO_INICIADA,EM_ANDAMENTO`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Extrair IDs únicos de solicitantes
        const uniqueIds = Array.from(new Set(data.data.map((d: any) => d.solicitante_id).filter(Boolean)));
        
        // Log detalhado para debug
        console.log('=== DEMANDAS ABERTAS (NAO_INICIADA ou EM_ANDAMENTO) ===');
        data.data.forEach((d: any) => {
          console.log(`ID: ${d.id} | Título: ${d.titulo} | Status: ${d.status} | Solicitante ID: ${d.solicitante_id} | Solicitante Nome: ${d.solicitante?.nome}`);
        });
        console.log('IDs únicos de contatos com demandas abertas:', uniqueIds);
        
        setContactsWithOpenDemands(uniqueIds as number[]);
        setTotalOpenDemandsCount(data.data.length);
      }
    } catch (error) {
      console.error('Erro ao buscar contatos com demandas abertas:', error);
    }
  };

  const handleContactClick = (contact: Eleitor) => {
    // On mobile (≤900px), open the dedicated mobile sheet instead
    if (typeof window !== 'undefined' && window.innerWidth <= 900) {
      setMobileDetailId(contact.id);
      return;
    }
    setSelectedContact(contact);
    setFormData({
      nome: contact.nome || '',
      telefone: contact.telefone || '',
      email: contact.email || '',
      cidade_id: contact.cidade_id?.toString() || '',
      data_nascimento: contact.data_nascimento || '',
      profissao: contact.profissao || '',
      time_coracao: contact.time_coracao || '',
      rede_social: contact.rede_social || '',
      observacoes: contact.observacoes || '',
      multiplicador: contact.multiplicador ?? false,
    });
    setHasChanges(false);
    setActiveTab('registros');
    setSelectedTags(contact.tags || []);
    setShowModal(true);
    if (contact.id) {
      fetchRegistros(contact.id);
      fetchDemandas(contact.id);
    }
  };

  const fetchContacts = async () => {
    if (!session?.access_token) return;
    
    try {
      setLoading(true);
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContacts(data.data || []);
        setLastFetched(new Date());
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('[fetchContacts] error', response.status, err);
        setContacts([]);
      }
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    if (!formData.telefone.trim()) {
      alert('Telefone é obrigatório');
      return;
    }

    if (!formData.cidade_id) {
      alert('Cidade é obrigatória');
      return;
    }

    if (!session?.access_token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    setSubmitting(true);

    try {
      const url = selectedContact 
        ? `${API_BASE_URL}/api/v1/eleitores/${selectedContact.id}`
        : `${API_BASE_URL}/api/v1/eleitores`;
      
      const method = selectedContact ? 'PUT' : 'POST';

      const body: Record<string, any> = { ...formData, tag_ids: selectedTags.map(t => t.id) };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setShowModal(false);
        setSelectedContact(null);
        setSelectedTags([]);
        setFormData({
          nome: '',
          telefone: '',
          email: '',
          cidade_id: '',
          data_nascimento: '',
          profissao: '',
          time_coracao: '',
          rede_social: '',
          observacoes: '',
          multiplicador: false,
        });
        setHasChanges(false);
        fetchContacts();
        fetchContactsWithOpenDemands();
      } else {
        const error = await response.json();
        alert(error.error || `Erro ao ${selectedContact ? 'atualizar' : 'criar'} contato`);
      }
    } catch (error) {
      console.error(`Erro ao ${selectedContact ? 'atualizar' : 'criar'} contato:`, error);
      alert(`Erro ao ${selectedContact ? 'atualizar' : 'criar'} contato`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDemandClick = (demandId: number) => {
    navigate('/demandas', { state: { openDemandId: demandId } });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedContact(null);
    setSelectedTags([]);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      cidade_id: '',
      data_nascimento: '',
      profissao: '',
      time_coracao: '',
      rede_social: '',
      observacoes: '',
      multiplicador: false,
    });
    setHasChanges(false);
    setActiveTab('registros');
    setRegistrosFilter('tudo');
    setRegistros([]);
    setDemandas([]);
    setNewRegistro('');
    setEditingRegistro(null);
    setEditingText('');
    
    // Recarregar lista de contatos com demandas abertas
    fetchContactsWithOpenDemands();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedContact || !session) return;
    
    setDeleting(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${selectedContact.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar contato');
      }

      // Remove from local state
      setContacts(prev => prev.filter(c => c.id !== selectedContact.id));
      setShowDeleteConfirm(false);
      setShowModal(false);
      setSelectedContact(null);
    } catch (error) {
      console.error('Erro ao deletar contato:', error);
      alert('Erro ao deletar contato');
    } finally {
      setDeleting(false);
    }
  };

  // Função para buscar demandas do contato
  const fetchDemandas = async (eleitorId: number) => {
    if (!session?.access_token) return;
    
    try {
      setLoadingDemandas(true);
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas?solicitante_id=${eleitorId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDemandas(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar demandas:', error);
    } finally {
      setLoadingDemandas(false);
    }
  };

  // Funções para Registros
  const fetchRegistros = async (eleitorId: number) => {
    if (!session?.access_token) return;
    
    try {
      setLoadingRegistros(true);
      setLoadingLog(true);
      const [regRes, logRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/v1/eleitores/${eleitorId}/registros`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
        apiFetch(`${API_BASE_URL}/api/v1/eleitores/${eleitorId}/historico`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
      ]);
      if (regRes.ok) {
        const data = await regRes.json();
        setRegistros(data.data || []);
      }
      if (logRes.ok) {
        const data = await logRes.json();
        setLogEntries(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao buscar registros:', error);
    } finally {
      setLoadingRegistros(false);
      setLoadingLog(false);
    }
  };

  const handleAddRegistro = async () => {
    if (!selectedContact?.id || !newRegistro.trim() || !session?.access_token) return;

    setAddingRegistro(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${selectedContact.id}/registros`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ descricao: newRegistro.trim() }),
      });

      if (response.ok) {
        setNewRegistro('');
        fetchRegistros(selectedContact.id);
      } else {
        alert('Erro ao adicionar registro');
      }
    } catch (error) {
      console.error('Erro ao adicionar registro:', error);
      alert('Erro ao adicionar registro');
    } finally {
      setAddingRegistro(false);
    }
  };

  const handleEditRegistro = async (registroId: number) => {
    if (!selectedContact?.id || !editingText.trim() || !session?.access_token) return;

    setSavingRegistroId(registroId);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${selectedContact.id}/registros/${registroId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ descricao: editingText.trim() }),
      });

      if (response.ok) {
        setEditingRegistro(null);
        setEditingText('');
        fetchRegistros(selectedContact.id);
      } else {
        alert('Erro ao editar registro');
      }
    } catch (error) {
      console.error('Erro ao editar registro:', error);
      alert('Erro ao editar registro');
    } finally {
      setSavingRegistroId(null);
    }
  };

  const handleDeleteRegistro = async (registroId: number) => {
    if (!selectedContact?.id || !session?.access_token) return;

    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;

    setDeletingRegistroId(registroId);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${selectedContact.id}/registros/${registroId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        fetchRegistros(selectedContact.id);
      } else {
        alert('Erro ao excluir registro');
      }
    } catch (error) {
      console.error('Erro ao excluir registro:', error);
      alert('Erro ao excluir registro');
    } finally {
      setDeletingRegistroId(null);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveField = async (fieldKey: string, body: Record<string, any>, localPatch?: Partial<Eleitor>) => {
    const contact = selectedContactRef.current;
    if (!contact || !session?.access_token) return;
    setSavingField(fieldKey);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${contact.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('Erro ao atualizar contato');
      const data = await response.json();
      const serverContact = data.data as Eleitor;
      // Use the server response as the base (authoritative), then re-apply any
      // local-only fields the server doesn't return (e.g. tags array)
      const currentContact = selectedContactRef.current ?? contact;
      const updated = {
        ...serverContact,
        tags: currentContact.tags,
      } as Eleitor;
      setSelectedContact(updated);
      setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
      fetchRegistros(contact.id);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar alteração');
    } finally {
      setSavingField(null);
    }
  };

  const saveContactTags = async (tags: Tag[]) => {
    if (!selectedContact || !session?.access_token) return;
    setSelectedTags(tags);
    setSavingField('tags');
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${selectedContact.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ tag_ids: tags.map(t => t.id) }),
      });
      if (!response.ok) throw new Error('Erro ao atualizar tags');
      const updated = { ...selectedContact, tags } as Eleitor;
      setSelectedContact(updated);
      setContacts(prev => prev.map(c => c.id === updated.id ? updated : c));
      fetchRegistros(selectedContact.id);
    } catch (error) {
      console.error('Erro ao salvar tags:', error);
      setSelectedTags(selectedContact.tags || []);
      alert('Erro ao salvar tags');
    } finally {
      setSavingField(null);
    }
  };

  // Toggle filter (only one active at a time)
  const toggleFilter = (filterType: 'birthdays' | 'expiring' | 'openDemands' | 'multiplicadores') => {
    if (filterType === 'birthdays') {
      setFilterBirthdays(!filterBirthdays);
      setFilterExpiring(false);
      setFilterOpenDemands(false);
      setFilterMultiplicadores(false);
    } else if (filterType === 'expiring') {
      setFilterExpiring(!filterExpiring);
      setFilterBirthdays(false);
      setFilterOpenDemands(false);
      setFilterMultiplicadores(false);
    } else if (filterType === 'openDemands') {
      setFilterOpenDemands(!filterOpenDemands);
      setFilterBirthdays(false);
      setFilterExpiring(false);
      setFilterMultiplicadores(false);
    } else if (filterType === 'multiplicadores') {
      setFilterMultiplicadores(!filterMultiplicadores);
      setFilterBirthdays(false);
      setFilterExpiring(false);
      setFilterOpenDemands(false);
    }
  };

  // Get active filter color
  const getActiveFilterColor = (): string => {
    if (filterBirthdays) return '#10b981'; // verde
    if (filterExpiring) return '#f59e0b'; // amarelo
    if (filterOpenDemands) return 'var(--accent)'; // azul
    return 'transparent';
  };

  // Get contact status color
  const getContactColor = (contact: Eleitor): string => {
    // Prioridade: aniversário > vencendo > demandas
    if (isBirthdayToday(contact.data_nascimento)) return '#10b981'; // verde
    // TODO: adicionar lógica de cadastro vencendo
    // if (isExpiring(contact)) return '#f59e0b'; // amarelo
    // TODO: adicionar lógica de demandas em aberto
    // if (hasOpenDemands(contact)) return 'var(--accent)'; // azul
    return 'transparent';
  };

  // Check if contact is birthday today
  const isBirthdayToday = (dateString: string | null): boolean => {
    if (!dateString) return false;
    
    const today = new Date();
    const todayDay = today.getDate();
    const todayMonth = today.getMonth() + 1;
    
    let birthDay: number;
    let birthMonth: number;
    
    if (typeof dateString === 'string') {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        birthMonth = parseInt(parts[1], 10);
        birthDay = parseInt(parts[2], 10);
      } else {
        const birthDate = new Date(dateString);
        birthDay = birthDate.getDate();
        birthMonth = birthDate.getMonth() + 1;
      }
    } else {
      const birthDate = new Date(dateString);
      birthDay = birthDate.getDate();
      birthMonth = birthDate.getMonth() + 1;
    }
    
    return birthDay === todayDay && birthMonth === todayMonth;
  };

  // Avatar color palette
  const avatarColorPalette = ['#8b5cf6', '#10b981', '#f97316', '#3b82f6', '#ec4899', '#06b6d4', '#f59e0b', '#ef4444'];
  const getAvatarColor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColorPalette[Math.abs(hash) % avatarColorPalette.length];
  };

  // Birthdays this week (next 7 days including today)
  const isBirthdayThisWeek = (dateString: string | null): boolean => {
    if (!dateString) return false;
    const today = new Date();
    const parts = dateString.split('-');
    if (parts.length !== 3) return false;
    const birthMonth = parseInt(parts[1], 10);
    const birthDay = parseInt(parts[2], 10);
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      if (d.getDate() === birthDay && (d.getMonth() + 1) === birthMonth) return true;
    }
    return false;
  };
  const birthdaysThisWeek = contacts.filter(c => isBirthdayThisWeek(c.data_nascimento)).length;

  // Contacts added this month
  const contactsThisMonth = contacts.filter(c => {
    if (!c.created_at) return false;
    const d = new Date(c.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  // Format birthday short (e.g. "14 de mai")
  const formatBirthday = (dateString: string | null): string => {
    if (!dateString) return '-';
    try {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const date = new Date(2000, month, day);
        return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '');
      }
      return '-';
    } catch { return '-'; }
  };

  const [sortColumn, setSortColumn] = useState<string>('nome');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Last updated text
  const lastUpdatedText = lastFetched
    ? (() => {
        const mins = Math.round((Date.now() - lastFetched.getTime()) / 60000);
        return mins < 1 ? 'atualizado agora' : `atualizado há ${mins} minuto${mins !== 1 ? 's' : ''}`;
      })()
    : '';

  // Today formatted (e.g. "15 DE MAIO")
  const todayFormatted = new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' }).toUpperCase();

  // Get birthday contacts
  const birthdayContacts = contacts.filter(contact => isBirthdayToday(contact.data_nascimento));

  const filteredContacts = useMemo(() => contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      contact.nome.toLowerCase().includes(searchLower) ||
      (contact.telefone && contact.telefone.includes(searchTerm)) ||
      (contact.cidade?.nome && contact.cidade.nome.toLowerCase().includes(searchLower));
    
    const matchesCity = !selectedCity || contact.cidade_id?.toString() === selectedCity;
    const matchesBirthday = !filterBirthdays || isBirthdayToday(contact.data_nascimento);
    const matchesExpiring = !filterExpiring;
    const matchesOpenDemands = !filterOpenDemands || contactsWithOpenDemands.includes(contact.id);
    const matchesMultiplicadores = !filterMultiplicadores || contact.multiplicador === true;
    const matchesTag = filterTagIds.length === 0 || (contact.tags || []).some(t => filterTagIds.includes(t.id));
    const matchesProfissao = !filterProfissao || contact.profissao === filterProfissao;
    const matchesAssessor = !filterAssessorId || contact.assessor?.id === filterAssessorId;
    
    return matchesSearch && matchesCity && matchesBirthday && matchesExpiring && matchesOpenDemands && matchesMultiplicadores && matchesTag && matchesProfissao && matchesAssessor;
  }).sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    if (sortColumn === 'nome') return dir * a.nome.localeCompare(b.nome);
    if (sortColumn === 'cidade') {
      const aN = a.cidade?.nome || '';
      const bN = b.cidade?.nome || '';
      return dir * aN.localeCompare(bN);
    }
    if (sortColumn === 'telefone') {
      return dir * (a.telefone || '').localeCompare(b.telefone || '');
    }
    if (sortColumn === 'aniversario') {
      const aD = a.data_nascimento ? a.data_nascimento.substring(5) : '';
      const bD = b.data_nascimento ? b.data_nascimento.substring(5) : '';
      return dir * aD.localeCompare(bD);
    }
    return 0;
  }), [contacts, searchTerm, selectedCity, filterBirthdays, filterExpiring, filterOpenDemands, contactsWithOpenDemands, filterMultiplicadores, filterTagIds, filterProfissao, filterAssessorId, sortColumn, sortDirection]);

  const uniqueAssessors = useMemo(() => {
    const map = new Map<number, { id: number; nome: string }>();
    contacts.forEach(c => { if (c.assessor) map.set(c.assessor.id, c.assessor); });
    return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [contacts]);

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Format date for registro (without time)
  const formatRegistroDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name: string): string => {
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Heatmap: count contacts per cidade_id
  const contactCounts = useMemo(() => {
    const acc: Record<number, number> = {};
    filteredContacts.forEach(c => {
      if (c.cidade_id) acc[c.cidade_id] = (acc[c.cidade_id] ?? 0) + 1;
    });
    return acc;
  }, [filteredContacts]);

  // Get unique cities from contacts
  const contactCities = Array.from(new Set(contacts.map(c => c.cidade_id).filter(Boolean)))
    .map(cityId => {
      const contact = contacts.find(c => c.cidade_id === cityId);
      return contact?.cidade ? { id: cityId, nome: contact.cidade.nome, uf: contact.cidade.uf } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a!.nome.localeCompare(b!.nome));

  // Reset visible window whenever filters change
  useEffect(() => {
    setVisibleCount(40);
  }, [searchTerm, selectedCity, filterBirthdays, filterExpiring, filterOpenDemands, filterTagIds, filterProfissao, filterMultiplicadores]);

  // Infinite scroll — only render the visible slice
  const hasMore = visibleCount < filteredContacts.length;
  const currentContacts = filteredContacts.slice(0, visibleCount);

  // IntersectionObserver: load next batch when sentinel enters viewport
  useEffect(() => {
    const sentinel = infiniteScrollSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          setLoadingMore(true);
          setTimeout(() => {
            setVisibleCount(prev => prev + 40);
            setLoadingMore(false);
          }, 350);
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore]);

  const uniqueProfissoes = useMemo(() =>
    (Array.from(new Set(contacts.map(c => c.profissao).filter(Boolean))) as string[]).sort(),
  [contacts]);

  const groupedContacts = useMemo(() => {
    const groups: { letter: string; items: Eleitor[]; isBirthday?: boolean }[] = [];
    const todayBirthdays = currentContacts.filter(c => isBirthdayToday(c.data_nascimento));
    if (todayBirthdays.length > 0) {
      groups.push({ letter: 'HOJE', items: todayBirthdays, isBirthday: true });
    }
    const nonBirthday = currentContacts.filter(c => !isBirthdayToday(c.data_nascimento));
    nonBirthday.forEach(contact => {
      const letter = contact.nome.trim()[0]?.toUpperCase() || '#';
      const last = groups[groups.length - 1];
      if (last && last.letter === letter && !last.isBirthday) {
        last.items.push(contact);
      } else {
        groups.push({ letter, items: [contact] });
      }
    });
    return groups;
  }, [currentContacts]);

  const formatPhone = (phone: string | null): string => {
    if (!phone) return '';
    const d = phone.replace(/\D/g, '');
    if (d.length === 13) return `(${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
    if (d.length === 12) return `(${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return phone;
  };

  const handleWhatsAppClick = (phone: string | null) => {
    if (!phone) return;
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const handleCallClick = (phone: string | null) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  if (permissionsLoading) {
    const shimmerDisplayMode = (() => {
      const saved = localStorage.getItem('displayMode');
      return saved === 'map' ? 'map' : 'list';
    })();
    return (
      <Container $mapMode={shimmerDisplayMode === 'map'}>
        <PageHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ShimmerBox width="180px" height="28px" />
            <ShimmerBox width="280px" height="16px" />
          </div>
          <ShimmerBox width="120px" height="40px" style={{ borderRadius: 8 }} />
        </PageHeader>
        <TableWrapper>
          {[...Array(10)].map((_, i) => (
            <ShimmerRow key={i}>
              <ShimmerBox width="40px" height="40px" style={{ borderRadius: '50%', flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <ShimmerBox width="160px" height="16px" />
                <ShimmerBox width="120px" height="13px" />
              </div>
              <ShimmerBox width="80px" height="13px" />
            </ShimmerRow>
          ))}
        </TableWrapper>
      </Container>
    );
  }

  if (!can('contacts.view.all') && !can('contacts.view.city') && !can('contacts.view.own')) {
    return (
      <Container $mapMode={false}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
          gap: 16,
          padding: 32
        }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: '#1e293b', margin: 0 }}>Acesso Negado</h2>
          <p style={{ fontSize: 16, color: '#64748b', textAlign: 'center', maxWidth: 400, margin: 0 }}>
            Você não tem permissão para visualizar os contatos.
          </p>
        </div>
      </Container>
    );
  }

  return (
    <Container $mapMode={viewMode === 'map'}>

      {/* ── Modo Mapa ── */}
      {viewMode === 'map' && (
        <MapFullScreen>
          {/* Título + busca — topo esquerdo */}
          <MapTopLeft>
            <MapTitleRow>
              <MapTitleIcon>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
                </svg>
              </MapTitleIcon>
              <MapPageTitle>Contatos</MapPageTitle>
            </MapTitleRow>
            <MapPageSubtitle>Gerencie todos os contatos da campanha</MapPageSubtitle>
            <MapTitleDivider />
            <MapSearchOverlay>
              <svg
                style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8', pointerEvents: 'none', zIndex: 1 }}
                fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <MapSearchInput
                type="text"
                placeholder="Buscar contato..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
              {searchTerm && (
                <MapSearchClearButton onClick={() => setSearchTerm('')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </MapSearchClearButton>
              )}
              {searchTerm.trim().length >= 2 && (() => {
                const results = contacts
                  .filter(c => c.nome.toLowerCase().includes(searchTerm.toLowerCase()))
                  .slice(0, 8);
                return results.length > 0 ? (
                  <MapSearchResults>
                    {results.map(contact => (
                      <MapSearchResultItem
                        key={contact.id}
                        onClick={() => {
                          handleContactClick(contact);
                          setShowModal(true);
                          setSearchTerm('');
                        }}
                      >
                        <MapResultAvatar>
                          {contact.nome.trim().split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()}
                        </MapResultAvatar>
                        <MapResultInfo>
                          <MapResultName>{contact.nome}</MapResultName>
                          {contact.cidade && (
                            <MapResultCity>{contact.cidade.nome} - {contact.cidade.uf}</MapResultCity>
                          )}
                        </MapResultInfo>
                      </MapSearchResultItem>
                    ))}
                  </MapSearchResults>
                ) : (
                  <MapSearchResults>
                    <MapSearchNoResult>Nenhum contato encontrado</MapSearchNoResult>
                  </MapSearchResults>
                );
              })()}
            </MapSearchOverlay>
          </MapTopLeft>

          {/* Novo Contato / Importar — topo direito */}
          <div style={{ position: 'absolute', top: 32, right: 40, zIndex: 20, display: 'flex', gap: '8px' }}>
            <PermissionGuard permission="contacts.import">
              <MapAddButton onClick={() => setShowImportModal(true)} style={{ position: 'static', background: 'white', color: '#334155', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Importar
              </MapAddButton>
            </PermissionGuard>
            <PermissionGuard permission="contacts.create">
              <MapAddButton onClick={() => setShowModal(true)} style={{ position: 'static' }}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Novo Contato
              </MapAddButton>
            </PermissionGuard>
          </div>

          {/* Mapa de calor */}
          <ContactsHeatMap
            contactCounts={contactCounts}
            loading={loading}
            onVisibleMaxChange={(max) => setVisibleMax(max)}
          />

          {/* Big numbers — canto esquerdo (vertical) */}
          <MapStatsOverlay $isHidden={searchTerm.trim().length >= 2}>
            <MapStatItem
              $active={false}
              onClick={() => { setFilterBirthdays(false); setFilterExpiring(false); setFilterOpenDemands(false); setFilterMultiplicadores(false); }}
            >
              <MapStatIcon $color="#10b981">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="60px" height="24px" />
                ) : (
                  <MapStatValue>{contacts.length}</MapStatValue>
                )}
                <MapStatLabel>Total de Contatos</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterOpenDemands}
              onClick={() => toggleFilter('openDemands')}
            >
              <MapStatIcon $color="var(--accent)">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterOpenDemands}>{contactsWithOpenDemands.length}</MapStatValue>
                )}
                <MapStatLabel>Demandas em Aberto</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterBirthdays}
              onClick={() => toggleFilter('birthdays')}
            >
              <MapStatIcon $color="#10b981">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 2C12 2 10.5 3.5 10.5 5C10.5 6.38071 11.6193 7.5 13 7.5C14.3807 7.5 15.5 6.38071 15.5 5C15.5 3.5 14 2 14 2M12 2C12 2 13.5 3.5 13.5 5C13.5 6.38071 12.3807 7.5 11 7.5C9.61929 7.5 8.5 6.38071 8.5 5C8.5 3.5 10 2 10 2M12 2V7.5M20 10H4C4 10 4 10 4 10C4 10 4 10 4 10V17C4 18.6569 5.34315 20 7 20H17C18.6569 20 20 18.6569 20 17V10Z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterBirthdays}>{birthdayContacts.length}</MapStatValue>
                )}
                <MapStatLabel>Aniversariantes Hoje</MapStatLabel>
              </MapStatContent>
            </MapStatItem>

            <MapStatItem
              $active={filterMultiplicadores}
              onClick={() => toggleFilter('multiplicadores')}
            >
              <MapStatIcon $color="#f59e0b">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </MapStatIcon>
              <MapStatContent>
                {loading ? (
                  <ShimmerLine width="50px" height="24px" />
                ) : (
                  <MapStatValue $active={filterMultiplicadores}>{contacts.filter(c => c.multiplicador).length}</MapStatValue>
                )}
                <MapStatLabel>Multiplicadores</MapStatLabel>
              </MapStatContent>
            </MapStatItem>
          </MapStatsOverlay>
        </MapFullScreen>
      )}

      {/* ── Modo Lista / Tabela ── */}
      {viewMode === 'table' && (
        <>
          <PageHeader>
            <PageHeaderText>
              <MobilePageTitleRow>
                <PageTitle>Contatos</PageTitle>
                <MobileHeaderButtons>
                  <PermissionGuard permission="contacts.import">
                    <MobileImportBtn onClick={() => setShowImportModal(true)}>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                        <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Importar
                    </MobileImportBtn>
                  </PermissionGuard>
                  <PermissionGuard permission="contacts.create">
                    <MobileNewBtn onClick={() => setShowModal(true)}>
                      <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="14" height="14">
                        <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Novo
                    </MobileNewBtn>
                  </PermissionGuard>
                </MobileHeaderButtons>
              </MobilePageTitleRow>
              <PageSubtitle>{contacts.length} contatos · toda a rede{lastUpdatedText ? ` · ${lastUpdatedText}` : ''}</PageSubtitle>
            </PageHeaderText>
            <DesktopHeaderButtons>
              <PermissionGuard permission="contacts.import">
                <AddButton
                  onClick={() => setShowImportModal(true)}
                  style={{ background: 'white', color: '#334155', border: '1.5px solid #e2e8f0', boxShadow: 'none' }}
                >
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Importar
                </AddButton>
              </PermissionGuard>
              <PermissionGuard permission="contacts.create">
                <AddButton onClick={() => setShowModal(true)}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Novo Contato
                </AddButton>
              </PermissionGuard>
            </DesktopHeaderButtons>
          </PageHeader>

          {birthdayContacts.length > 0 && !birthdayBannerDismissed && (
            <BirthdayBanner>
              <BirthdayBannerIcon>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c2600a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z"/>
                  <path d="M2 11h20"/>
                  <path d="M7 11V9a5 5 0 0110 0v2"/>
                  <path d="M12 2v3"/>
                  <path d="M10 5c0-1.1.9-2 2-2s2 .9 2 2"/>
                </svg>
              </BirthdayBannerIcon>
              <BirthdayBannerBody>
                <BirthdayBannerDate>{todayFormatted} · HOJE</BirthdayBannerDate>
                <BirthdayBannerTitle>
                  <span style={{ color: '#c2600a', fontWeight: 800 }}>{birthdayContacts.length} contato{birthdayContacts.length !== 1 ? 's' : ''}</span>{' '}faz{birthdayContacts.length !== 1 ? 'em' : ''} aniversário hoje
                </BirthdayBannerTitle>
              </BirthdayBannerBody>
              <BirthdayBannerRight>
                <BirthdayAvatarRow>
                  {birthdayContacts.slice(0, 3).map(c => (
                    <BirthdayAvatar key={c.id} style={{ background: getAvatarColor(c.nome) }} title={c.nome}>
                      {getUserInitials(c.nome)}
                    </BirthdayAvatar>
                  ))}
                  {birthdayContacts.length > 3 && (
                    <BirthdayAvatarMore>+{birthdayContacts.length - 3}</BirthdayAvatarMore>
                  )}
                </BirthdayAvatarRow>
              </BirthdayBannerRight>
              <BirthdayBannerClose onClick={() => setBirthdayBannerDismissed(true)} title="Fechar">×</BirthdayBannerClose>
            </BirthdayBanner>
          )}

          <StatsGrid>
            <StatCard
              $active={!filterBirthdays && !filterOpenDemands && !filterMultiplicadores}
              $accentColor="#3b82f6"
              onClick={() => { setFilterBirthdays(false); setFilterExpiring(false); setFilterOpenDemands(false); setFilterMultiplicadores(false); }}
            >
              <StatCardTop>
                <StatDot $color="#3b82f6" />
                <StatCardLabel>Total de Contatos</StatCardLabel>
              </StatCardTop>
              {loading ? <ShimmerLine width="60px" height="30px" style={{ margin: '4px 0 2px' }} /> : <StatCardValue>{contacts.length}</StatCardValue>}
              <StatCardSub>{contactsThisMonth > 0 ? `+${contactsThisMonth} este mês` : 'todos os contatos'}</StatCardSub>
            </StatCard>

            <StatCard
              $active={filterBirthdays}
              $accentColor="#ec4899"
              onClick={() => toggleFilter('birthdays')}
            >
              <StatCardTop>
                <StatDot $color="#ec4899" />
                <StatCardLabel>Aniversariantes Hoje</StatCardLabel>
              </StatCardTop>
              {loading ? <ShimmerLine width="40px" height="30px" style={{ margin: '4px 0 2px' }} /> : <StatCardValue>{birthdayContacts.length}</StatCardValue>}
              <StatCardSub>+{birthdaysThisWeek} esta semana</StatCardSub>
            </StatCard>

            <StatCard
              $active={filterMultiplicadores}
              $accentColor="#f59e0b"
              onClick={() => toggleFilter('multiplicadores')}
            >
              <StatCardTop>
                <StatDot $color="#f59e0b" />
                <StatCardLabel>Multiplicadores</StatCardLabel>
              </StatCardTop>
              {loading ? <ShimmerLine width="40px" height="30px" style={{ margin: '4px 0 2px' }} /> : <StatCardValue>{contacts.filter(c => c.multiplicador).length}</StatCardValue>}
              <StatCardSub>recrutaram 0 contatos</StatCardSub>
            </StatCard>

            <StatCard
              $active={filterOpenDemands}
              $accentColor="var(--accent)"
              onClick={() => toggleFilter('openDemands')}
            >
              <StatCardTop>
                <StatDot $color="var(--accent)" />
                <StatCardLabel>Com Demanda em Aberto</StatCardLabel>
              </StatCardTop>
              {loading ? <ShimmerLine width="40px" height="30px" style={{ margin: '4px 0 2px' }} /> : <StatCardValue>{contactsWithOpenDemands.length}</StatCardValue>}
              <StatCardSub>de {totalOpenDemandsCount} vinculadas</StatCardSub>
            </StatCard>
          </StatsGrid>

          {/* Mobile: contact sync card (Contact Picker API – only on supporting devices) */}
          <ContactSyncCard onImported={fetchContacts} />

          {/* Mobile: search + filter button */}
          <MobileSearchRow>
            <MobileSearchInputWrapper>
              <MobileSearchIcon>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </MobileSearchIcon>
              <MobileContactSearchInput
                type="text"
                placeholder="Buscar nome, telefone, cidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <MobileSearchClear onClick={() => setSearchTerm('')}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </MobileSearchClear>
              )}
            </MobileSearchInputWrapper>
            <MobileFilterButton
              $active={selectedCity !== '' || filterTagIds.length > 0 || filterProfissao !== '' || filterMultiplicadores}
              onClick={() => setShowFilterSheet(true)}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              {(selectedCity !== '' || filterTagIds.length > 0 || filterProfissao !== '' || filterMultiplicadores) && (
                <MobileFilterBadge />
              )}
            </MobileFilterButton>
          </MobileSearchRow>

          {/* Mobile: static filter chips */}
          <MobileFilterChipsRow>
            <MobileFilterChip $active={filterTagIds.length > 0} onClick={() => setShowFilterSheet(true)}>
              Tags
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </MobileFilterChip>
            <MobileFilterChip $active={selectedCity !== ''} onClick={() => setShowFilterSheet(true)}>
              Cidade
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </MobileFilterChip>
            <MobileFilterChip $active={filterProfissao !== ''} onClick={() => setShowFilterSheet(true)}>
              Profissão
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </MobileFilterChip>
            <MobileFilterChipStar $active={filterMultiplicadores} onClick={() => toggleFilter('multiplicadores')}>
              ★ Multiplicadores
            </MobileFilterChipStar>
          </MobileFilterChipsRow>

          {/* Mobile: active filter chips */}
          {(selectedCity !== '' || filterTagIds.length > 0 || filterProfissao !== '' || filterMultiplicadores || filterBirthdays) && (
            <MobileActiveFiltersRow>
              {filterBirthdays && (
                <MobileActiveChip $color="#ec4899" $bg="#fdf2f8" onClick={() => setFilterBirthdays(false)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z"/><path d="M2 11h20"/><path d="M7 11V9a5 5 0 0110 0v2"/><path d="M12 2v3"/></svg>
                  Aniversariantes<MobileChipRemove>×</MobileChipRemove>
                </MobileActiveChip>
              )}
              {filterMultiplicadores && (
                <MobileActiveChip $color="#92400e" $bg="#fef3c7" onClick={() => setFilterMultiplicadores(false)}>
                  ★ Multiplicadores<MobileChipRemove>×</MobileChipRemove>
                </MobileActiveChip>
              )}
              {selectedCity !== '' && (
                <MobileActiveChip $color="#1e40af" $bg="#eff6ff" onClick={() => setSelectedCity('')}>
                  📍 {sortedCities.find(c => c.id.toString() === selectedCity)?.nome || 'Cidade'}<MobileChipRemove>×</MobileChipRemove>
                </MobileActiveChip>
              )}
              {filterProfissao !== '' && (
                <MobileActiveChip $color="#5b21b6" $bg="#f5f3ff" onClick={() => setFilterProfissao('')}>
                  {filterProfissao}<MobileChipRemove>×</MobileChipRemove>
                </MobileActiveChip>
              )}
              {filterTagIds.map(id => {
                const tag = allGroupTags.find(t => t.id === id);
                if (!tag) return null;
                const c = getTagColor(tag.cor);
                return (
                  <MobileActiveChip key={id} $color={c.text} $bg={c.bg} onClick={() => setFilterTagIds(prev => prev.filter(tid => tid !== id))}>
                    {tag.nome}<MobileChipRemove>×</MobileChipRemove>
                  </MobileActiveChip>
                );
              })}
            </MobileActiveFiltersRow>
          )}

          {/* Mobile: filter bottom sheet */}
          {showFilterSheet && (
            <>
              <FilterSheetOverlay onClick={() => setShowFilterSheet(false)} />
              <FilterSheet>
                <FilterSheetHandle />
                <FilterSheetHeader>
                  <FilterSheetTitle>Filtros</FilterSheetTitle>
                  <FilterSheetClearAll onClick={() => {
                    setSelectedCity(''); setFilterTagIds([]); setFilterProfissao('');
                    setFilterMultiplicadores(false); setFilterBirthdays(false);
                  }}>
                    Limpar tudo
                  </FilterSheetClearAll>
                </FilterSheetHeader>

                <FilterSheetBody>
                  {/* Especiais */}
                  <FilterSheetSection>
                    <FilterSheetSectionLabel>Especiais</FilterSheetSectionLabel>
                    <FilterSheetRow onClick={() => toggleFilter('birthdays')}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z"/><path d="M2 11h20"/><path d="M7 11V9a5 5 0 0110 0v2"/><path d="M12 2v3"/></svg>
                      <FilterSheetRowLabel>Aniversariantes hoje</FilterSheetRowLabel>
                      <FilterSheetRowCheck $active={filterBirthdays}>
                        {filterBirthdays && <svg width="12" height="12" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </FilterSheetRowCheck>
                    </FilterSheetRow>
                    <FilterSheetRow onClick={() => toggleFilter('multiplicadores')}>
                      <span style={{ fontSize: 16, lineHeight: 1, color: '#f59e0b' }}>★</span>
                      <FilterSheetRowLabel>Multiplicadores</FilterSheetRowLabel>
                      <FilterSheetRowCheck $active={filterMultiplicadores}>
                        {filterMultiplicadores && <svg width="12" height="12" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </FilterSheetRowCheck>
                    </FilterSheetRow>
                  </FilterSheetSection>

                  {/* Cidade */}
                  <FilterSheetSection>
                    <FilterSheetSectionLabel>Cidade</FilterSheetSectionLabel>
                    <FilterSheetSearchInput
                      type="text"
                      placeholder="Buscar cidade..."
                      value={citySearchTerm}
                      onChange={e => setCitySearchTerm(e.target.value)}
                    />
                    <FilterSheetOptionsList>
                      <FilterSheetOption $active={selectedCity === ''} onClick={() => setSelectedCity('')}>
                        Todas as cidades
                        {selectedCity === '' && <svg width="16" height="16" fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </FilterSheetOption>
                      {contactCities
                        .filter(c => c && c.nome.toLowerCase().includes(citySearchTerm.toLowerCase()))
                        .map(city => city && (
                          <FilterSheetOption key={city.id} $active={selectedCity === city.id?.toString()} onClick={() => setSelectedCity(city.id?.toString() || '')}>
                            {city.nome}
                            {selectedCity === city.id?.toString() && <svg width="16" height="16" fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </FilterSheetOption>
                        ))}
                    </FilterSheetOptionsList>
                  </FilterSheetSection>

                  {/* Tags */}
                  {allGroupTags.length > 0 && (
                    <FilterSheetSection>
                      <FilterSheetSectionLabel>Tags</FilterSheetSectionLabel>
                      <FilterSheetTagsGrid>
                        {allGroupTags.map(tag => {
                          const isActive = filterTagIds.includes(tag.id);
                          const c = getTagColor(tag.cor);
                          return (
                            <FilterSheetTagChip
                              key={tag.id}
                              $active={isActive}
                              $bg={c.bg}
                              $text={c.text}
                              $border={c.border}
                              onClick={() => setFilterTagIds(prev => isActive ? prev.filter(id => id !== tag.id) : [...prev, tag.id])}
                            >
                              {tag.nome}
                            </FilterSheetTagChip>
                          );
                        })}
                      </FilterSheetTagsGrid>
                    </FilterSheetSection>
                  )}

                  {/* Profissão */}
                  {uniqueProfissoes.length > 0 && (
                    <FilterSheetSection>
                      <FilterSheetSectionLabel>Profissão</FilterSheetSectionLabel>
                      <FilterSheetOptionsList>
                        <FilterSheetOption $active={filterProfissao === ''} onClick={() => setFilterProfissao('')}>
                          Todas as profissões
                          {filterProfissao === '' && <svg width="16" height="16" fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </FilterSheetOption>
                        {uniqueProfissoes.map(prof => (
                          <FilterSheetOption key={prof} $active={filterProfissao === prof} onClick={() => setFilterProfissao(filterProfissao === prof ? '' : prof)}>
                            {prof}
                            {filterProfissao === prof && <svg width="16" height="16" fill="none" stroke="var(--accent)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </FilterSheetOption>
                        ))}
                      </FilterSheetOptionsList>
                    </FilterSheetSection>
                  )}
                </FilterSheetBody>

                <FilterSheetApply onClick={() => setShowFilterSheet(false)}>
                  Ver {filteredContacts.length} contato{filteredContacts.length !== 1 ? 's' : ''}
                </FilterSheetApply>
              </FilterSheet>
            </>
          )}

          <FiltersRow>
            <SearchBarWrapper>
              <SearchIconWrap>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/></svg>
              </SearchIconWrap>
              <SearchBar
                type="text"
                placeholder="Buscar por nome, telefone ou cidade..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <SearchBarClearButton type="button" onClick={() => setSearchTerm('')}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </SearchBarClearButton>
              )}
            </SearchBarWrapper>

            <ContactFilterGroup>
              {/* City filter chip */}
              <CitySelectContainer data-city-select>
                <FilterChip
                  $active={selectedCity !== ''}
                  onClick={(e) => { e.stopPropagation(); setIsCityDropdownOpen(!isCityDropdownOpen); }}
                >
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                  {selectedCity ? sortedCities.find(c => c.id.toString() === selectedCity)?.nome || 'Cidade' : 'Cidade'}
                  {selectedCity
                    ? <ChipX onClick={(e) => { e.stopPropagation(); setSelectedCity(''); }}>×</ChipX>
                    : <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                  }
                </FilterChip>
                {isCityDropdownOpen && (
                  <CityDropdown>
                    <CitySearchInput
                      type="text"
                      placeholder="Buscar cidade..."
                      value={citySearchTerm}
                      onChange={(e) => setCitySearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <CityOptionsList>
                      <CityOption $active={selectedCity === ''} onClick={() => { setSelectedCity(''); setIsCityDropdownOpen(false); setCitySearchTerm(''); }}>
                        Todas as Cidades
                      </CityOption>
                      {sortedCities
                        .filter(city => city.nome.toLowerCase().includes(citySearchTerm.toLowerCase()))
                        .map(city => (
                          <CityOption key={city.id} $active={selectedCity === city.id.toString()} onClick={() => { setSelectedCity(city.id.toString()); setIsCityDropdownOpen(false); setCitySearchTerm(''); }}>
                            {city.nome}
                          </CityOption>
                        ))}
                    </CityOptionsList>
                  </CityDropdown>
                )}
              </CitySelectContainer>

              {/* Assessor / user filter chip */}
              {uniqueAssessors.length > 0 && (
                <CitySelectContainer data-assessor-select>
                  <FilterChip
                    $active={filterAssessorId !== null}
                    onClick={(e) => { e.stopPropagation(); setIsAssessorDropdownOpen(!isAssessorDropdownOpen); }}
                  >
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6"/></svg>
                    {filterAssessorId !== null ? uniqueAssessors.find(a => a.id === filterAssessorId)?.nome || 'Usuário' : 'Usuário'}
                    {filterAssessorId !== null
                      ? <ChipX onClick={(e) => { e.stopPropagation(); setFilterAssessorId(null); }}>×</ChipX>
                      : <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                    }
                  </FilterChip>
                  {isAssessorDropdownOpen && (
                    <CityDropdown>
                      <CitySearchInput
                        type="text"
                        placeholder="Buscar usuário..."
                        value={assessorSearchTerm}
                        onChange={(e) => setAssessorSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <CityOptionsList>
                        <CityOption $active={filterAssessorId === null} onClick={() => { setFilterAssessorId(null); setIsAssessorDropdownOpen(false); setAssessorSearchTerm(''); }}>
                          Todos os usuários
                        </CityOption>
                        {uniqueAssessors
                          .filter(a => a.nome.toLowerCase().includes(assessorSearchTerm.toLowerCase()))
                          .map(a => (
                            <CityOption key={a.id} $active={filterAssessorId === a.id} onClick={() => { setFilterAssessorId(a.id); setIsAssessorDropdownOpen(false); setAssessorSearchTerm(''); }}>
                              {a.nome}
                            </CityOption>
                          ))}
                      </CityOptionsList>
                    </CityDropdown>
                  )}
                </CitySelectContainer>
              )}

              {/* Tags filter chip */}
              {allGroupTags.length > 0 && (
                <CitySelectContainer data-tag-select>
                  <FilterChip
                    $active={filterTagIds.length > 0}
                    onClick={(e) => { e.stopPropagation(); setIsTagDropdownOpen(!isTagDropdownOpen); }}
                  >
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                    {filterTagIds.length === 0 ? 'Tags' : filterTagIds.length === 1 ? allGroupTags.find(t => t.id === filterTagIds[0])?.nome || 'Tag' : `${filterTagIds.length} Tags`}
                    {filterTagIds.length > 0
                      ? <ChipX onClick={(e) => { e.stopPropagation(); setFilterTagIds([]); }}>×</ChipX>
                      : <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 10, height: 10 }}><path d="M19 9l-7 7-7-7"/></svg>
                    }
                  </FilterChip>
                  {isTagDropdownOpen && (
                    <CityDropdown>
                      <CitySearchInput
                        type="text"
                        placeholder="Buscar tag..."
                        value={tagSearchTerm}
                        onChange={(e) => setTagSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <CityOptionsList>
                        {allGroupTags
                          .filter(t => t.nome.toLowerCase().includes(tagSearchTerm.toLowerCase()))
                          .map(tag => {
                            const isActive = filterTagIds.includes(tag.id);
                            const c = getTagColor(tag.cor);
                            return (
                              <CityOption key={tag.id} $active={isActive} onClick={() => setFilterTagIds(prev => isActive ? prev.filter(id => id !== tag.id) : [...prev, tag.id])}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.bg, border: `1.5px solid ${c.border}`, display: 'inline-block', flexShrink: 0 }} />
                                  {tag.nome}
                                </span>
                                {isActive && <span style={{ marginLeft: 'auto', color: 'var(--accent)' }}>✓</span>}
                              </CityOption>
                            );
                          })}
                      </CityOptionsList>
                    </CityDropdown>
                  )}
                </CitySelectContainer>
              )}

              {/* Multiplicadores toggle chip */}
              <FilterChip $active={filterMultiplicadores} onClick={() => setFilterMultiplicadores(p => !p)}>
                <svg fill={filterMultiplicadores ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Multiplicadores
                {filterMultiplicadores && <ChipX onClick={(e) => { e.stopPropagation(); setFilterMultiplicadores(false); }}>×</ChipX>}
              </FilterChip>
            </ContactFilterGroup>
          </FiltersRow>

          {/* ── Mobile Contact List ── */}
          {loading && (
            <MobileContactList>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <MobileContactShimmer key={i}>
                  <MobileShimmerAvatarBox />
                  <MobileShimmerContent>
                    <ShimmerLine width="140px" height="15px" />
                    <ShimmerLine width="100px" height="12px" style={{ marginTop: '6px' }} />
                    <ShimmerLine width="120px" height="11px" style={{ marginTop: '4px' }} />
                  </MobileShimmerContent>
                </MobileContactShimmer>
              ))}
            </MobileContactList>
          )}
          {!loading && filteredContacts.length > 0 && (
            <MobileContactList>
              {groupedContacts.map(({ letter, items, isBirthday }) => (
                <React.Fragment key={letter}>
                  <AlphaSectionHeader $isBirthday={!!isBirthday}>
                    <AlphaSectionLetter $isBirthday={!!isBirthday}>
                      {isBirthday ? (
                        <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginRight:4}}><path d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z"/><path d="M2 11h20"/><path d="M7 11V9a5 5 0 0110 0v2"/><path d="M12 2v3"/></svg>HOJE · {items.length}</>
                      ) : letter}
                    </AlphaSectionLetter>
                    <AlphaSectionCount>{items.length}/{filteredContacts.length}</AlphaSectionCount>
                  </AlphaSectionHeader>
                  {items.map((contact) => (
                    <MobileContactCard key={contact.id} $birthday={isBirthdayToday(contact.data_nascimento)} onClick={() => handleContactClick(contact)}>
                      <MobileCardAvatarWrap>
                        <MobileCardAvatar style={{ background: getAvatarColor(contact.nome) }}>
                          {getUserInitials(contact.nome)}
                        </MobileCardAvatar>
                        {contact.multiplicador && <MobileCardMultiBadge>⭐</MobileCardMultiBadge>}
                      </MobileCardAvatarWrap>
                      <MobileCardBody>
                        <MobileCardNameRow>
                          <MobileCardName>{contact.nome}</MobileCardName>
                          {isBirthdayToday(contact.data_nascimento) && (
                            <MobileCardBirthdayBadge>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M4 11h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8z"/><path d="M2 11h20"/><path d="M7 11V9a5 5 0 0110 0v2"/><path d="M12 2v3"/></svg>
                              HOJE
                            </MobileCardBirthdayBadge>
                          )}
                          <MobileCardMenuBtn onClick={e => { e.stopPropagation(); handleContactClick(contact); }}>
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                            </svg>
                          </MobileCardMenuBtn>
                        </MobileCardNameRow>
                        {(contact.profissao || contact.cidade) && (
                          <MobileCardSubRow>
                            {contact.profissao && <span>{contact.profissao}</span>}
                            {contact.profissao && contact.cidade && <span style={{ margin: '0 4px', color: '#e2e8f0' }}>·</span>}
                            {contact.cidade && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0, color: '#94a3b8' }}>
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                                {contact.cidade.nome}
                              </span>
                            )}
                          </MobileCardSubRow>
                        )}
                        {!contact.cidade_id && contact.observacoes?.includes('📒 Importado do telefone') && (
                          <MobileCardSubRow style={{ marginTop: 3 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: '#fffbeb', color: '#92400e',
                              border: '1px solid #fcd34d',
                              borderRadius: 6, padding: '2px 7px',
                              fontSize: 11, fontWeight: 700, letterSpacing: '0.01em',
                            }}>
                              ⚠ Completar
                            </span>
                            <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>📒 falta cidade</span>
                          </MobileCardSubRow>
                        )}
                        {(contact.tags || []).length > 0 && (
                          <MobileCardTagsRow>
                            {(contact.tags || []).slice(0, 3).map(tag => {
                              const c = getTagColor(tag.cor);
                              return (
                                <MobileCardTag key={tag.id} style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                                  {tag.nome}
                                </MobileCardTag>
                              );
                            })}
                          </MobileCardTagsRow>
                        )}
                        {contact.telefone && (
                          <MobileCardPhoneRow>
                            <MobileCardPhone>{formatPhone(contact.telefone)}</MobileCardPhone>
                            <MobileCardActions>
                              <MobileCardActionBtn $color="#25d366" onClick={e => { e.stopPropagation(); handleWhatsAppClick(contact.telefone); }}>
                                <svg fill="currentColor" viewBox="0 0 24 24" width="17" height="17">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                              </MobileCardActionBtn>
                              <MobileCardActionBtn $color="#3b82f6" onClick={e => { e.stopPropagation(); handleCallClick(contact.telefone); }}>
                                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="17" height="17">
                                  <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                                </svg>
                              </MobileCardActionBtn>
                            </MobileCardActions>
                          </MobileCardPhoneRow>
                        )}
                      </MobileCardBody>
                    </MobileContactCard>
                  ))}
                </React.Fragment>
              ))}
            </MobileContactList>
          )}

          {loading ? (
        <TableWrapper>
          <Table>
            <TableHead>
              <TableHeader $sortable onClick={() => handleSort('nome')}>Nome <SortIcon $active={sortColumn === 'nome'} $direction={sortDirection} /></TableHeader>
              <TableHeader $sortable onClick={() => handleSort('cidade')}>Cidade · Profissão <SortIcon $active={sortColumn === 'cidade'} $direction={sortDirection} /></TableHeader>
              <TableHeader $sortable onClick={() => handleSort('telefone')}>Telefone <SortIcon $active={sortColumn === 'telefone'} $direction={sortDirection} /></TableHeader>
              <TableHeader $sortable onClick={() => handleSort('aniversario')}>Aniversário <SortIcon $active={sortColumn === 'aniversario'} $direction={sortDirection} /></TableHeader>
              <TableHeader>Ações</TableHeader>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <TableRow key={i}>
                  <TableCell style={{ padding: '14px 0' }}>
                    <NameCellWithAvatar>
                      <ShimmerCircle style={{ width: '36px', height: '36px', flexShrink: 0 }} />
                      <NameCell>
                        <ShimmerLine width="150px" height="16px" />
                      </NameCell>
                    </NameCellWithAvatar>
                  </TableCell>
                  <TableCell style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: '14px 0' }}>
                    <ShimmerLine width="100px" height="14px" />
                    <ShimmerLine width="80px" height="12px" />
                  </TableCell>
                  <TableCell style={{ padding: '14px 0' }}>
                    <ShimmerLine width="100px" height="14px" />
                  </TableCell>
                  <TableCell style={{ padding: '14px 0' }}>
                    <ShimmerLine width="80px" height="14px" />
                  </TableCell>
                  <TableCell style={{ padding: '14px 0' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <ShimmerCircle style={{ width: '32px', height: '32px' }} />
                      <ShimmerCircle style={{ width: '32px', height: '32px' }} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      ) : filteredContacts.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </EmptyIcon>
          <EmptyText>
            {searchTerm ? 'Nenhum contato encontrado' : 'Nenhum contato cadastrado ainda'}
          </EmptyText>
          {!searchTerm && (
            <EmptyButton onClick={() => setShowModal(true)}>
              Criar primeiro contato
            </EmptyButton>
          )}
        </EmptyState>
      ) : (
        <>
          <TableWrapper>
            <Table>
              <TableHead>
                <TableHeader $sortable onClick={() => handleSort('nome')}>Nome <SortIcon $active={sortColumn === 'nome'} $direction={sortDirection} /></TableHeader>
                <TableHeader $sortable onClick={() => handleSort('cidade')}>Cidade · Profissão <SortIcon $active={sortColumn === 'cidade'} $direction={sortDirection} /></TableHeader>
                <TableHeader $sortable onClick={() => handleSort('telefone')}>Telefone <SortIcon $active={sortColumn === 'telefone'} $direction={sortDirection} /></TableHeader>
                <TableHeader $sortable onClick={() => handleSort('aniversario')}>Aniversário <SortIcon $active={sortColumn === 'aniversario'} $direction={sortDirection} /></TableHeader>
                <TableHeader>Ações</TableHeader>
              </TableHead>
              <TableBody>
                {currentContacts.map((contact) => (
                  <TableRow 
                    key={contact.id}
                    $borderColor={getContactColor(contact)}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      handleContactClick(contact);
                    }}
                  >
                    <TableCell style={{ padding: '14px 0' }}>
                      <NameCellWithAvatar>
                        <ContactAvatar style={{ background: getAvatarColor(contact.nome), fontSize: 13, width: 36, height: 36, minWidth: 36 }}>
                          {getUserInitials(contact.nome)}
                        </ContactAvatar>
                        <NameCell>
                          <NameRow>
                            <span>{contact.nome}</span>
                            {contact.multiplicador && (
                              <span style={{ color: '#f59e0b', display: 'inline-flex', alignItems: 'center' }} title="Multiplicador Político">
                                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              </span>
                            )}
                          </NameRow>
                          {(contact.tags || []).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 2 }}>
                              {contact.tags!.map(tag => {
                                const c = getTagColor(tag.cor);
                                return (
                                  <span key={tag.id} style={{ padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                                    {tag.nome}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </NameCell>
                      </NameCellWithAvatar>
                    </TableCell>
                    <TableCell style={{ padding: '14px 0' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {contact.cidade && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569', fontSize: 13 }}>
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, color: '#94a3b8' }}>
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor" stroke="none"/>
                            </svg>
                            {contact.cidade.nome}
                          </span>
                        )}
                        {contact.profissao && (
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{contact.profissao}</span>
                        )}
                        {!contact.cidade && !contact.profissao && '-'}
                      </div>
                    </TableCell>
                    <TableCell style={{ padding: '14px 0', fontSize: 13 }}>{contact.telefone || '-'}</TableCell>
                    <TableCell style={{ padding: '14px 0' }}>
                      {contact.data_nascimento ? (
                        <BirthdayCellContent>
                          <span style={{ color: isBirthdayToday(contact.data_nascimento) ? '#ef4444' : 'inherit', fontWeight: isBirthdayToday(contact.data_nascimento) ? 600 : 'inherit' }}>
                            {formatBirthday(contact.data_nascimento)}
                          </span>
                          {isBirthdayToday(contact.data_nascimento) && <TodayBadge>Hoje</TodayBadge>}
                        </BirthdayCellContent>
                      ) : '-'}
                    </TableCell>
                    <TableCell style={{ padding: '14px 0' }}>
                      <ContactActions>
                        {contact.telefone ? (
                          <>
                            <ActionButton onClick={() => handleWhatsAppClick(contact.telefone)} title="WhatsApp">
                              <svg fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                            </ActionButton>
                            <ActionButton onClick={() => handleCallClick(contact.telefone)} title="Ligar">
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </ActionButton>
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '13px' }}>-</span>
                        )}
                      </ContactActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>

          {/* Infinite scroll sentinel */}
          <div ref={infiniteScrollSentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <div style={{ padding: '4px 0 16px' }}>
              {[...Array(5)].map((_, i) => (
                <ShimmerRow key={i}>
                  <ShimmerBox width="40px" height="40px" style={{ borderRadius: '50%', flexShrink: 0 }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <ShimmerBox width="160px" height="16px" />
                    <ShimmerBox width="120px" height="13px" />
                  </div>
                  <ShimmerBox width="80px" height="13px" />
                </ShimmerRow>
              ))}
            </div>
          )}
        </>
      )}
        </>
      )}

      {/* ── NEW CONTACT: redesigned centered modal ── */}
      {!selectedContact && (
        <NewContactModal
          isOpen={showModal && !selectedContact}
          onClose={handleCloseModal}
          onSubmit={handleCreateContact}
          formData={formData}
          onChange={handleFormChange}
          submitting={submitting}
          sortedCities={sortedCities}
          selectedTags={selectedTags}
          setSelectedTags={(tags) => { setSelectedTags(tags); setHasChanges(true); }}
          allGroupTags={allGroupTags}
          setAllGroupTags={setAllGroupTags}
        />
      )}

      <ModalOverlay $isOpen={showModal && !!selectedContact} onClick={handleCloseModal}>
        {/* ── EXISTING CONTACT: rendered below ── */}

        {/* ── EXISTING CONTACT: full 2-column detail modal ── */}
        {selectedContact && (
          <ContactDetailModal onClick={(e) => e.stopPropagation()}>

            {/* ── HEADER ── */}
            <CDHeader>
              <CDHeaderTop>
                <CDMeta>
                  <CDMetaChip>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12">
                      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    CONTATO
                  </CDMetaChip>
                  <CDMetaDot>•</CDMetaDot>
                  <CDMetaText>#{String(selectedContact.id).padStart(4, '0')}</CDMetaText>
                  {selectedContact.cidade && (
                    <>
                      <CDMetaDot>•</CDMetaDot>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="12" height="12" style={{ color: '#6b7280', flexShrink: 0 }}>
                        <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <CDMetaText>{selectedContact.cidade.nome}</CDMetaText>
                    </>
                  )}
                  {selectedContact.profissao && (
                    <>
                      <CDMetaDot>•</CDMetaDot>
                      <CDMetaText>{selectedContact.profissao}</CDMetaText>
                    </>
                  )}
                </CDMeta>
                <CDHeaderActions>
                  <CloseButton onClick={handleCloseModal}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </CloseButton>
                </CDHeaderActions>
              </CDHeaderTop>
              <CDHeaderBottom>
                <CDNameRow>
                  <CDName>
                    <InlineEditText
                      value={selectedContact.nome || ''}
                      canEdit={canEditContact}
                      saving={savingField === 'nome'}
                      onSave={(v) => saveField('nome', { nome: v })}
                      placeholder="Nome completo"
                      allowEmpty={false}
                    />
                  </CDName>
                  {selectedContact.multiplicador && (
                    <CDMultiplicadorBadge>
                      <svg fill="currentColor" viewBox="0 0 20 20" width="12" height="12">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      MULTIPLICADOR
                    </CDMultiplicadorBadge>
                  )}
                </CDNameRow>
                <CDActionBtns>
                  {selectedContact.telefone && (
                    <>
                      <CDActionBtn $primary onClick={() => handleWhatsAppClick(selectedContact.telefone)}>
                        <svg fill="currentColor" viewBox="0 0 24 24" width="15" height="15">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        WhatsApp
                      </CDActionBtn>
                      <CDActionBtn $call onClick={() => handleCallClick(selectedContact.telefone)}>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="15" height="15">
                          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Ligar
                      </CDActionBtn>
                    </>
                  )}
                  <CDActionBtn onClick={() => navigate('/demandas', { state: { openNewDemand: true, solicitanteId: selectedContact.id } })}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="15" height="15">
                      <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Nova demanda
                  </CDActionBtn>
                </CDActionBtns>
              </CDHeaderBottom>
            </CDHeader>

            {/* ── BODY ── */}
            <CDBody>

              {/* LEFT: info panel */}
              <CDLeft>

                <CDInfoColumns>
                {/* CONTATO */}
                <CDSection>
                  <CDSectionTitle>CONTATO</CDSectionTitle>
                  <CDInfoGrid>
                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Telefone
                      </CDInfoLabel>
                      <CDInfoValue style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <InlineEditText
                          value={selectedContact.telefone || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'telefone'}
                          onSave={(v) => saveField('telefone', { telefone: v || null })}
                          placeholder="(00) 00000-0000"
                          emptyLabel="-"
                          renderDisplay={(v) => <span>{v === '-' ? '-' : formatPhone(v)}</span>}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditText
                          value={selectedContact.email || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'email'}
                          onSave={(v) => saveField('email', { email: v || null })}
                          placeholder="email@exemplo.com"
                          emptyLabel="-"
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Cidade
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditSelect
                          value={selectedContact.cidade_id != null ? String(selectedContact.cidade_id) : undefined}
                          canEdit={canEditContact}
                          saving={savingField === 'cidade_id'}
                          options={sortedCities.map(c => ({ value: String(c.id), label: `${c.nome} - ${c.microrregiao.mesorregiao.UF.sigla}` }))}
                          searchable
                          searchPlaceholder="Buscar cidade..."
                          emptyLabel="-"
                          renderDisplay={() => <span>{selectedContact.cidade ? `${selectedContact.cidade.nome} - ${selectedContact.cidade.uf}` : '-'}</span>}
                          onSave={(v) => {
                            const id = parseInt(v, 10);
                            const city = sortedCities.find(c => c.id === id);
                            return saveField('cidade_id', { cidade_id: id }, {
                              cidade_id: id,
                              cidade: city ? { id, nome: city.nome, uf: city.microrregiao.mesorregiao.UF.sigla } : undefined,
                            });
                          }}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Nascimento
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditDate
                          value={selectedContact.data_nascimento ?? undefined}
                          canEdit={canEditContact}
                          saving={savingField === 'data_nascimento'}
                          onSave={(v) => saveField('data_nascimento', { data_nascimento: v })}
                          emptyLabel="-"
                          renderDisplay={(formatted) => {
                            if (!selectedContact.data_nascimento || formatted === '-') return <span>-</span>;
                            const parts = selectedContact.data_nascimento.slice(0, 10).split('-');
                            const birthDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                            const today = new Date();
                            const age = today.getFullYear() - birthDate.getFullYear() - (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate()) ? 1 : 0);
                            const isToday = birthDate.getDate() === today.getDate() && birthDate.getMonth() === today.getMonth();
                            return (
                              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {birthDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })} · {age} anos
                                {isToday && <CDTodayBadge>Hoje</CDTodayBadge>}
                              </span>
                            );
                          }}
                        />
                      </CDInfoValue>
                    </CDInfoRow>
                  </CDInfoGrid>
                </CDSection>

                {/* SOBRE */}
                <CDSection>
                  <CDSectionTitle>SOBRE</CDSectionTitle>
                  <CDInfoGrid>
                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Profissão
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditText
                          value={selectedContact.profissao || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'profissao'}
                          onSave={(v) => saveField('profissao', { profissao: v || null })}
                          placeholder="Profissão"
                          emptyLabel="-"
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Time do coração
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditSelect
                          value={selectedContact.time_coracao ?? undefined}
                          canEdit={canEditContact}
                          saving={savingField === 'time_coracao'}
                          options={timesRS.map(t => ({ value: t, label: t }))}
                          emptyLabel="-"
                          onSave={(v) => saveField('time_coracao', { time_coracao: v })}
                        />
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Multiplicador
                      </CDInfoLabel>
                      <CDInfoValue>
                        <HoverShell $canEdit={canEditContact} onClick={() => canEditContact && saveField('multiplicador', { multiplicador: !selectedContact.multiplicador })}>
                          {savingField === 'multiplicador' ? <SpinnerCircle /> : (
                            <MultiplicadorBadge $active={selectedContact.multiplicador ?? false}>
                              {selectedContact.multiplicador ? 'Sim' : 'Não'}
                            </MultiplicadorBadge>
                          )}
                        </HoverShell>
                      </CDInfoValue>
                    </CDInfoRow>

                    <CDInfoRow style={{ alignItems: 'flex-start', minHeight: 'auto', paddingTop: 4 }}>
                      <CDInfoLabel style={{ paddingTop: 4 }}>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        Tags
                      </CDInfoLabel>
                      <CDInfoValue style={{ flexWrap: 'wrap', alignItems: 'flex-start', paddingTop: 2 }}>
                        <TagSelector
                          selectedTags={selectedTags}
                          onChange={saveContactTags}
                          onTagCreated={(tag) => setAllGroupTags(prev => [...prev, tag].sort((a, b) => a.nome.localeCompare(b.nome)))}
                          disabled={!canEditContact}
                        />
                      </CDInfoValue>
                    </CDInfoRow>
                  </CDInfoGrid>
                </CDSection>

                </CDInfoColumns>

                {/* REDES SOCIAIS */}
                <CDSection style={{ maxWidth: 'calc(50% - 12px)' }}>
                  <CDSectionTitle>REDES SOCIAIS</CDSectionTitle>
                  <CDInfoGrid>
                    <CDInfoRow>
                      <CDInfoLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Rede Social
                      </CDInfoLabel>
                      <CDInfoValue>
                        <InlineEditText
                          value={selectedContact.rede_social || ''}
                          canEdit={canEditContact}
                          saving={savingField === 'rede_social'}
                          onSave={(v) => saveField('rede_social', { rede_social: v || null })}
                          placeholder="@usuario ou link"
                          emptyLabel="-"
                        />
                      </CDInfoValue>
                    </CDInfoRow>
                  </CDInfoGrid>
                </CDSection>

                {/* NOTAS INTERNAS */}
                <CDSection>
                  <CDSectionTitle>NOTAS INTERNAS</CDSectionTitle>
                  <InlineEditTextArea
                    value={selectedContact.observacoes || ''}
                    canEdit={canEditContact}
                    saving={savingField === 'observacoes'}
                    onSave={(v) => saveField('observacoes', { observacoes: v || null })}
                    placeholder="Anotações e observações sobre o contato..."
                    emptyLabel="Sem notas internas"
                  />
                </CDSection>

                {/* CADASTRADO POR */}
                <CDCreatorRow>
                  <CDCreatorPill>
                    {selectedContact.assessor && (
                      <CDCreatorAvatar>
                        {selectedContact.assessor.nome.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                      </CDCreatorAvatar>
                    )}
                    <CDCreatorInfo>
                      {selectedContact.assessor && (
                        <CDCreatorLabel>CADASTRADO POR</CDCreatorLabel>
                      )}
                      <CDCreatorName>
                        {selectedContact.assessor ? selectedContact.assessor.nome : null}
                        {selectedContact.data_cadastro && (
                          <CDCreatorDate>
                            {new Date(selectedContact.data_cadastro + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </CDCreatorDate>
                        )}
                      </CDCreatorName>
                    </CDCreatorInfo>
                  </CDCreatorPill>
                </CDCreatorRow>

                  {canDeleteContact && (
                    <CDDangerZone>
                      <CDDangerDesc>Este contato não pode ser recuperado após exclusão.</CDDangerDesc>
                      <CDDangerBtn onClick={handleDeleteClick} disabled={deleting}>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Excluir contato
                      </CDDangerBtn>
                    </CDDangerZone>
                  )}

              </CDLeft>

              {/* RIGHT: registros / demandas panel */}
              <CDRight>
                {/* ── Date grouping helper ── */}
                {(() => {
                  const groupByDate = (items: typeof registros) => {
                    const groups: { label: string; items: typeof registros }[] = [];
                    const seen = new Map<string, number>();
                    const today = new Date(); today.setHours(0,0,0,0);
                    const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
                    items.forEach(r => {
                      const d = new Date(r.created_at); d.setHours(0,0,0,0);
                      const dd = String(d.getDate()).padStart(2,'0'), mm = String(d.getMonth()+1).padStart(2,'0');
                      const key = `${dd}/${mm}/${d.getFullYear()}`;
                      let label = `${dd}/${mm}`;
                      if (d.getTime() === today.getTime()) label = `HOJE · ${dd}/${mm}`;
                      else if (d.getTime() === yesterday.getTime()) label = `ONTEM · ${dd}/${mm}`;
                      if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ label, items: [] }); }
                      groups[seen.get(key)!].items.push(r);
                    });
                    return groups;
                  };
                  return null;
                })()}

                <CDRightTabs>
                  <CDRightTab $active={activeTab === 'registros'} onClick={() => setActiveTab('registros')}>
                    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" width="14" height="14">
                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Registros {(registros.length + logEntries.length) > 0 && <CDRightTabCount>{registros.length + logEntries.length}</CDRightTabCount>}
                  </CDRightTab>
                  <CDRightTab $active={activeTab === 'demandas'} onClick={() => setActiveTab('demandas')}>
                    <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" width="14" height="14">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Demandas {demandas.length > 0 && <CDRightTabCount>{demandas.length}</CDRightTabCount>}
                  </CDRightTab>
                </CDRightTabs>

                {activeTab === 'registros' && (() => {
                  type CommentItem = Registro & { _source: 'comment' };
                  type HistoryItem = LogEntry & { _source: 'history'; created_at: string };
                  type TimelineEntry = CommentItem | HistoryItem;

                  const allItems: TimelineEntry[] = [
                    ...registros.map(r => ({ ...r, _source: 'comment' as const })),
                    ...logEntries.map(l => ({ ...l, _source: 'history' as const, created_at: l.data_hora })),
                  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

                  const filtered = registrosFilter === 'comentarios'
                    ? allItems.filter(i => i._source === 'comment')
                    : registrosFilter === 'historico'
                    ? allItems.filter(i => i._source === 'history')
                    : allItems;

                  const today = new Date(); today.setHours(0,0,0,0);
                  const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
                  const groups: { label: string; items: TimelineEntry[] }[] = [];
                  const seen = new Map<string, number>();
                  filtered.forEach(r => {
                    const d = new Date(r.created_at); d.setHours(0,0,0,0);
                    const dd = String(d.getDate()).padStart(2,'0'), mm = String(d.getMonth()+1).padStart(2,'0');
                    const key = `${d.getFullYear()}-${mm}-${dd}`;
                    let label = `${dd}/${mm}`;
                    if (d.getTime() === today.getTime()) label = `HOJE · ${dd}/${mm}`;
                    else if (d.getTime() === yesterday.getTime()) label = `ONTEM · ${dd}/${mm}`;
                    if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ label, items: [] }); }
                    groups[seen.get(key)!].items.push(r);
                  });

                  const isLoading = loadingRegistros || loadingLog;
                  return (
                    <>
                      <CDFilterBar>
                        <CDFilterChips>
                          <CDFilterChipBox $active={registrosFilter === 'tudo'} onClick={() => setRegistrosFilter('tudo')}>Tudo</CDFilterChipBox>
                          <CDFilterChipText $active={registrosFilter === 'comentarios'} onClick={() => setRegistrosFilter('comentarios')}>Comentários</CDFilterChipText>
                          <CDFilterChipText $active={registrosFilter === 'historico'} onClick={() => setRegistrosFilter('historico')}>Histórico</CDFilterChipText>
                        </CDFilterChips>
                      </CDFilterBar>

                      <CDRightBody>
                        {isLoading ? (
                          <>
                            {[1,2,3].map((i) => (
                              <ShimmerRegistroItem key={i}>
                                <ShimmerRegistroHeader>
                                  <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                                    <ShimmerAvatar style={{ width:32, height:32, minWidth:32 }} />
                                    <div style={{ flex:1 }}>
                                      <ShimmerLine width="120px" height="13px" />
                                      <ShimmerLine width="80px" height="11px" style={{ marginTop:5 }} />
                                    </div>
                                  </div>
                                </ShimmerRegistroHeader>
                                <ShimmerLine width="100%" height="13px" />
                                <ShimmerLine width="80%" height="13px" style={{ marginTop:6 }} />
                              </ShimmerRegistroItem>
                            ))}
                          </>
                        ) : filtered.length === 0 ? (
                          <CDEmptyState>
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                            <span>Nenhum registro ainda</span>
                          </CDEmptyState>
                        ) : (
                          <CDTimeline>
                            {groups.map(({ label, items }, gi) => (
                              <React.Fragment key={label}>
                                <CDDateGroup>{label}</CDDateGroup>
                                {items.map((entry, idx) => {
                                  const isLast = gi === groups.length - 1 && idx === items.length - 1;
                                  const time = new Date(entry.created_at).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });

                                  if (entry._source === 'history') {
                                    const authorName = (entry as HistoryItem).registrou?.nome || 'Sistema';
                                    return (
                                      <CDTimelineItem key={`log-${entry.id}`}>
                                        <CDTimelineLeft>
                                          <CDIconAvatar $type="history">
                                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="13" height="13">
                                              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </CDIconAvatar>
                                          {!isLast && <CDTimelineLine />}
                                        </CDTimelineLeft>
                                        <CDTimelineContent>
                                          <CDFeedRow>
                                            <CDFeedMeta>
                                              <CDFeedAuthor>{authorName}</CDFeedAuthor>
                                              <CDFeedAction> {(entry as HistoryItem).observacao}</CDFeedAction>
                                            </CDFeedMeta>
                                            <CDFeedTime>{time}</CDFeedTime>
                                          </CDFeedRow>
                                        </CDTimelineContent>
                                      </CDTimelineItem>
                                    );
                                  }

                                  const registro = entry as CommentItem;
                                  return (
                                    <CDTimelineItem key={`reg-${registro.id}`}>
                                      <CDTimelineLeft>
                                        <CDIconAvatar $type="comment">
                                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="13" height="13">
                                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </CDIconAvatar>
                                        {!isLast && <CDTimelineLine />}
                                      </CDTimelineLeft>
                                      <CDTimelineContent>
                                        <CDFeedRow>
                                          <CDFeedMeta>
                                            <CDFeedAuthor>{registro.usuario.nome}</CDFeedAuthor>
                                            <CDFeedAction> comentou</CDFeedAction>
                                          </CDFeedMeta>
                                          <CDFeedTime>{time}</CDFeedTime>
                                          {userData?.id && registro.usuario.id === userData.id && (
                                            <CDRegistroMenuWrap>
                                              <CDRegistroMenuBtn onClick={() => setOpenMenuId(openMenuId === registro.id ? null : registro.id)}>
                                                <svg fill="currentColor" viewBox="0 0 24 24" width="14" height="14">
                                                  <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                                </svg>
                                              </CDRegistroMenuBtn>
                                              {openMenuId === registro.id && (
                                                <RegistroMenu>
                                                  <RegistroMenuItem onClick={() => { setEditingRegistro(registro.id); setEditingText(registro.descricao); setOpenMenuId(null); }}>
                                                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                    Editar
                                                  </RegistroMenuItem>
                                                  <RegistroMenuItem $danger onClick={() => { setOpenMenuId(null); handleDeleteRegistro(registro.id); }} disabled={deletingRegistroId === registro.id}>
                                                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    Excluir
                                                  </RegistroMenuItem>
                                                </RegistroMenu>
                                              )}
                                            </CDRegistroMenuWrap>
                                          )}
                                        </CDFeedRow>
                                        {editingRegistro === registro.id ? (
                                          <EditRegistroForm style={{ marginTop: 6 }}>
                                            <EditTextArea value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={3} />
                                            <EditActions>
                                              <SaveButton onClick={() => handleEditRegistro(registro.id)} disabled={savingRegistroId === registro.id}>
                                                {savingRegistroId === registro.id ? <Spinner /> : 'Salvar'}
                                              </SaveButton>
                                              <CancelEditButton onClick={() => { setEditingRegistro(null); setEditingText(''); }}>Cancelar</CancelEditButton>
                                            </EditActions>
                                          </EditRegistroForm>
                                        ) : (
                                          <CDFeedCard>{registro.descricao}</CDFeedCard>
                                        )}
                                      </CDTimelineContent>
                                    </CDTimelineItem>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                          </CDTimeline>
                        )}
                      </CDRightBody>

                      <CDRightFooter>
                        <CDInputBox>
                          <CDInputArea
                            value={newRegistro}
                            onChange={(e) => setNewRegistro(e.target.value)}
                            placeholder="Anote uma conversa, lembrete ou observação..."
                            rows={3}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddRegistro();
                            }}
                          />
                          <CDFooterRow>
                            <CDSendBtn onClick={handleAddRegistro} disabled={!newRegistro.trim() || addingRegistro}>
                              {addingRegistro ? 'Enviando...' : 'Registrar'}
                            </CDSendBtn>
                          </CDFooterRow>
                        </CDInputBox>
                      </CDRightFooter>
                    </>
                  );
                })()}

                {activeTab === 'demandas' && (
                  <CDRightBody>
                    {loadingDemandas ? (
                      <CDEmptyState><span>Carregando...</span></CDEmptyState>
                    ) : demandas.length === 0 ? (
                      <CDEmptyState>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Nenhuma demanda encontrada</span>
                      </CDEmptyState>
                    ) : (
                      demandas.map((demanda: any) => (
                        <DemandaItem key={demanda.id} onClick={() => handleDemandClick(demanda.id)}>
                          <DemandaHeader>
                            <DemandaTitle>{demanda.titulo}</DemandaTitle>
                            <DemandaStatus $status={demanda.status}>
                              {demanda.status === 'NAO_INICIADA' && 'Não Iniciada'}
                              {demanda.status === 'EM_ANDAMENTO' && 'Em Andamento'}
                              {demanda.status === 'PAUSADA' && 'Pausada'}
                              {demanda.status === 'CONCLUIDA' && 'Concluída'}
                              {demanda.status === 'CANCELADA' && 'Cancelada'}
                            </DemandaStatus>
                          </DemandaHeader>
                          {demanda.descricao && <DemandaDescription>{demanda.descricao}</DemandaDescription>}
                          <DemandaFooter>
                            <DemandaInfo>
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                                <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              {new Date(demanda.created_at).toLocaleDateString('pt-BR')}
                            </DemandaInfo>
                            {demanda.tipo && (
                              <DemandaInfo>
                                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                                  <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {demanda.tipo}
                              </DemandaInfo>
                            )}
                          </DemandaFooter>
                        </DemandaItem>
                      ))
                    )}
                  </CDRightBody>
                )}
              </CDRight>
            </CDBody>
          </ContactDetailModal>
        )}
      </ModalOverlay>


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <ConfirmHeader>
              <ConfirmIcon>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </ConfirmIcon>
              <ConfirmTitle>Excluir Contato</ConfirmTitle>
            </ConfirmHeader>
            <ConfirmContent>
              <ConfirmText>
                Tem certeza que deseja excluir o contato <strong>{selectedContact?.nome}</strong>?
              </ConfirmText>
              <ConfirmWarning>
                Esta ação não pode ser desfeita. Todos os dados relacionados serão removidos permanentemente.
              </ConfirmWarning>
            </ConfirmContent>
            <ConfirmActions>
              <ConfirmCancelButton onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancelar
              </ConfirmCancelButton>
              <ConfirmDeleteButton onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir Contato'}
              </ConfirmDeleteButton>
            </ConfirmActions>
          </ConfirmModal>
        </ConfirmOverlay>
      )}

      {/* Import Contacts Modal */}
      <ImportContactsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          fetchContacts();
          fetchContactsWithOpenDemands();
        }}
      />

      {/* Mobile contact detail sheet */}
      <ContactDetailModalMobile
        isOpen={!!mobileDetailId}
        contatoId={mobileDetailId}
        onClose={() => setMobileDetailId(null)}
      />
    </Container>
  );
};

export default Contacts;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ShimmerRegistroItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
`;

const ShimmerRegistroHeader = styled.div`
  margin-bottom: 12px;
`;

const ShimmerAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const ShimmerLine = styled.div<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '14px'};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const ShimmerCircle = styled.div`
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

// ── Birthday Banner ──
const BirthdayBanner = styled.div`
  background: linear-gradient(to right, #fff8e6 0%, #ffecd2 40%, #ffd6e8 100%);
  border: 1.5px solid #fde68a;
  border-radius: 16px;
  padding: 10px 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 640px) {
    border-radius: 14px;
    padding: 14px 14px;
  }
`;

const BirthdayBannerIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
  line-height: 1;
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.55);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BirthdayBannerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

const BirthdayBannerDate = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: #c2600a;
  letter-spacing: 0.7px;
  text-transform: uppercase;
`;

const BirthdayBannerTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
`;

const BirthdayBannerRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

const BirthdayAvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const BirthdayAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  border: 2px solid white;
  margin-right: -6px;
  flex-shrink: 0;
`;

const BirthdayAvatarMore = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  border: 2px solid white;
  margin-left: 6px;
  flex-shrink: 0;
`;

const BirthdayBannerClose = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.06);
  color: #92400e;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  margin-left: 8px;

  &:hover { background: rgba(0,0,0,0.12); }
`;

// ── Stat Card New Components ──
const StatCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const StatSubtitle = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 1px;
`;

// ── Table New Components ──
const NameCellWithAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BirthdayCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TodayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
`;

// ── Filter Active Pill ──
const FilterActivePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fef3c7;
  border: 1.5px solid #fcd34d;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: #92400e;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: #fde68a;
    border-color: #f59e0b;
  }
`;

const FilterActivePillRemove = styled.span`
  font-size: 16px;
  line-height: 1;
  margin-left: 2px;
  opacity: 0.7;
  font-weight: 400;
`;

// ── Page Title Badge ──
const ContactCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-left: 8px;
  vertical-align: middle;
`;

const Container = styled.div<{ $mapMode?: boolean }>`
  width: 100%;
  min-height: 100vh;
  background: ${p => p.$mapMode ? '#ffffff' : '#f7f8fa'};
  padding: ${p => p.$mapMode ? '0' : '32px 40px'};
  padding-bottom: ${p => p.$mapMode ? '0' : '120px'};
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${p => p.$mapMode ? '0' : '16px 16px 100px 16px'};
    background: ${p => p.$mapMode ? '#ffffff' : '#f5f6f8'};
  }
`;

const Header = styled.div`
  margin-bottom: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ContactCount = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: #64748b;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    background: transparent;
    border: none;
    border-radius: 0;
    gap: 10px;
    overflow: visible;
  }

  @media (max-width: 640px) {
    display: flex;
    overflow-x: auto;
    background: transparent;
    border: none;
    border-radius: 0;
    gap: 10px;
    padding: 2px 0 10px;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const StatCard = styled.div<{ $active: boolean; $accentColor?: string }>`
  padding: 16px 20px;
  border-right: 1px solid #f1f5f9;
  border-bottom: 3px solid ${p => p.$active && p.$accentColor ? p.$accentColor : 'transparent'};
  background: ${p => p.$active ? '#f8fafc' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s;

  &:last-child { border-right: none; }
  &:hover { background: #f8fafc; }

  @media (max-width: 900px) {
    border-right: none;
    border-radius: 14px;
    border: 1.5px solid ${p => p.$active && p.$accentColor ? p.$accentColor + '55' : '#e8edf2'};
    background: ${p => p.$active && p.$accentColor
      ? `linear-gradient(to bottom, ${p.$accentColor}14 0%, #ffffff 100%)`
      : 'linear-gradient(to bottom, #eef0f5 0%, #ffffff 100%)'};
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  @media (max-width: 640px) {
    flex-shrink: 0;
    min-width: 120px;
    padding: 10px 14px;
    border-radius: 12px;
  }
`;

const StatCardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;

  @media (max-width: 640px) {
    font-size: 10px;
    letter-spacing: 0.4px;
  }
`;

const StatCardValue = styled.div`
  font-size: 30px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;
  margin-bottom: 2px;

  @media (max-width: 640px) {
    font-size: 22px;
  }
`;

const StatCardSub = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;

  @media (max-width: 640px) {
    font-size: 10px;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: white;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Table = styled.div`
  width: 100%;
`;

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 160px 150px 130px 110px;
  padding: 10px 20px 10px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 14px 14px 0 0;
`;

const TableBody = styled.div``;

const TableRow = styled.div<{ $borderColor?: string }>`
  display: grid;
  grid-template-columns: 1fr 160px 150px 130px 110px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.12s;
  cursor: pointer;
  padding-left: 24px;
  padding-right: 20px;

  &:hover { background: #f8fafc; }
  &:last-child { border-bottom: none; border-radius: 0 0 14px 14px; }
`;

const TableHeader = styled.div<{ $sortable?: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: ${p => p.$sortable ? 'pointer' : 'default'};
  user-select: none;
  ${p => p.$sortable && '&:hover { color: #64748b; }'}
`;

const SortIcon = styled.span<{ $active: boolean; $direction: 'asc' | 'desc' }>`
  display: inline-flex;
  font-size: 10px;
  opacity: ${p => p.$active ? 1 : 0.3};
  &::before { content: '${p => p.$direction === 'asc' ? '▲' : '▼'}'; }
`;

const TableCell = styled.div`
  font-size: 13.5px;
  color: #374151;
  font-weight: 500;
  display: flex;
  align-items: center;
  padding: 0;
  min-width: 0;
  overflow: hidden;
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  font-weight: 600;
  color: #1e293b;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ContactFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  border: 1px solid ${p => p.$active ? 'rgba(var(--accent-rgb), 0.4)' : '#e2e8f0'};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: ${p => p.$active ? '600' : '500'};
  color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  svg { width: 13px; height: 13px; }
  &:hover { border-color: rgba(var(--accent-rgb), 0.4); color: var(--accent); background: rgba(var(--accent-rgb), 0.06); }
`;

const ChipX = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.2);
  color: var(--accent);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: rgba(var(--accent-rgb), 0.35); }
`;

const CitySelectContainer = styled.div`
  position: relative;

  @media (max-width: 768px) {
    flex-shrink: 0;
  }
`;

const CitySelectButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'white'};
  border: 1px solid ${p => p.$active ? 'rgba(var(--accent-rgb), 0.3)' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$active ? 'var(--accent)' : '#475569'};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  touch-action: manipulation;

  .city-filter-icon { display: none; }
  .city-label-short { display: none; }
  .city-label-full  { display: inline; }

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: rgba(var(--accent-rgb), 0.08);
  }

  @media (hover: none), (pointer: coarse) {
    &:hover {
      border-color: ${p => p.$active ? '#bfdbfe' : '#e2e8f0'};
      color: ${p => p.$active ? '#1e40af' : '#475569'};
      background: ${p => p.$active ? '#eff6ff' : 'white'};
    }
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    gap: 8px;
    background: ${p => p.$active ? '#eff6ff' : 'white'};
    border-color: ${p => p.$active ? '#bfdbfe' : '#e2e8f0'};

    .city-filter-icon { display: block; width: 15px; height: 15px; }
    .city-label-full  { display: none; }
    .city-label-short { display: inline; }
  }
`;

const CityDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  min-width: 280px;
  max-width: 280px;
  max-height: 320px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CitySearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #1e293b;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-bottom-color: var(--accent);
  }
`;

const CityOptionsList = styled.div`
  overflow-y: auto;
  max-height: 260px;
`;

const CityOption = styled.div<{ $active: boolean }>`
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$active ? '#1e40af' : '#1e293b'};
  background: ${p => p.$active ? '#eff6ff' : 'transparent'};
  cursor: pointer;
  transition: background 0.1s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:hover {
    background: ${p => p.$active ? '#dbeafe' : '#f8fafc'};
  }

  @media (hover: none), (pointer: coarse) {
    &:hover {
      background: ${p => p.$active ? '#eff6ff' : 'transparent'};
    }
  }
`;

const SearchBarWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;
  max-width: 480px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  svg { width: 15px; height: 15px; }
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 9px 32px 9px 36px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #374151;
  font-size: 13.5px;
  font-weight: 500;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;

  &::placeholder { color: #94a3b8; font-weight: 400; }
  &:focus { outline: none; border-color: #93c5fd; box-shadow: 0 0 0 3px rgba(147,197,253,0.25); }
`;

const SearchBarClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  transition: color 0.2s ease;
  border-radius: 50%;

  &:hover {
    color: #64748b;
    background: #f1f5f9;
  }

  svg { width: 14px; height: 14px; }
`;

const ContactAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
`;



const ContactActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: var(--accent);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: var(--accent);
    color: white;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;

  svg {
    width: 40px;
    height: 40px;
  }
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 16px 0;
`;

const EmptyButton = styled.button`
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AddButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    align-items: center;
    margin-bottom: 16px;
  }
`;

const PageHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 28px;
    letter-spacing: -0.8px;
  }
`;

const MobilePageTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MobileCountPill = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 14px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    font-size: 16px;
    font-weight: 700;
    color: #475569;
  }
`;

const DesktopHeaderButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileHeaderButtons = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const MobileImportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  background: white;
  color: #334155;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;

  &:active { background: #f8fafc; }
`;

const MobileNewBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;

  &:active { opacity: 0.85; }
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-top: 2px;
  }
`;

const AddButton = styled.button`
  background: var(--accent);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.15s;
  white-space: nowrap;
  letter-spacing: -0.1px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15), 0 4px 12px rgba(var(--accent-rgb), 0.3);

  svg {
    width: 17px;
    height: 17px;
    stroke-width: 2.5;
  }

  &:hover {
    background: var(--accent-dark);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15), 0 6px 16px rgba(var(--accent-rgb), 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
    gap: 6px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 16px;
  color: #64748b;
`;

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2001;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${props => props.$isOpen ? 'fadeIn 0.2s ease' : 'none'};

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.9)'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  transition: transform 0.3s ease, opacity 0.3s ease;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    height: 92vh;
    border-radius: 20px 20px 0 0;
    transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(100%)'};
    opacity: 1;
  }
`;

const SideSheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SideSheetTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SideSheetSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 4px 0 0 0;
`;

const SideSheetContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    color: #1e293b;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const Input = styled.input`
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1e293b;
  font-size: 15px;
  transition: all 0.2s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: white;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1e293b;
  font-size: 15px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    background: white;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1e293b;
  font-size: 15px;
  transition: all 0.2s ease;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: white;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const SheetFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }
`;

const SheetFooterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SheetCancelButton = styled.button`
  flex-shrink: 0;
  padding: 12px 20px;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #e2e8f0;
    color: #475569;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const DeleteButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: white;
  color: #ef4444;
  border: 2px solid #ef4444;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Confirmation Modal Styles
const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2003;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ConfirmHeader = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ConfirmIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  svg { width: 28px; height: 28px; }
`;

const ConfirmTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ConfirmContent = styled.div`
  padding: 0 24px 24px;
  text-align: center;
`;

const ConfirmText = styled.p`
  font-size: 15px;
  color: #374151;
  margin: 0 0 12px 0;
  line-height: 1.5;
  strong { color: #111827; font-weight: 600; }
`;

const ConfirmWarning = styled.p`
  font-size: 13px;
  color: #dc2626;
  margin: 0;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const ConfirmCancelButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ConfirmDeleteButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: #b91c1c; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const CDDangerZone = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const CDDangerDesc = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.5;
`;

const CDDangerBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: none;
  color: #ef4444;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.2s;
  svg { width: 14px; height: 14px; }
  &:hover:not(:disabled) { color: #dc2626; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #e2e8f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 24px 0;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
    background: #f0f9ff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
`;

// Styled Components para Tabs e Registros
const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  gap: 8px;

  @media (max-width: 768px) {
    padding: 0 16px;
    gap: 0;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 16px 20px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? 'var(--accent)' : 'transparent'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: var(--accent);
  }

  .tab-label-short { display: none; }
  .tab-label-full  { display: inline; }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    padding: 12px 6px;
    font-size: 12px;
    gap: 4px;

    svg {
      width: 15px;
      height: 15px;
    }

    .tab-label-full  { display: none; }
    .tab-label-short { display: inline; }
  }
`;

const RegistrosContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
`;

const LoadingRegistros = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  font-size: 14px;
`;

const RegistrosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyRegistros = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 14px;
`;

const EmptyRegistrosState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyRegistrosIcon = styled.div`
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  color: #cbd5e1;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyRegistrosText = styled.p`
  color: #94a3b8;
  font-size: 15px;
  margin: 0;
`;

const RegistroItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
`;

const RegistroHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const RegistroUserInfo = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
`;

const RegistroUser = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const RegistroDate = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const RegistroMenuContainer = styled.div`
  position: relative;
`;

const RegistroMenuButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const RegistroMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 140px;
  margin-top: 4px;
  overflow: hidden;
`;

const RegistroMenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  background: none;
  border: none;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  color: ${props => props.$danger ? '#ef4444' : '#334155'};
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? '#fef2f2' : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegistroText = styled.p`
  font-size: 14px;
  color: #334155;
  margin: 0 0 12px 0;
  line-height: 1.6;
  white-space: pre-wrap;
`;



const EditRegistroForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditTextArea = styled.textarea`
  padding: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1e293b;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SaveButton = styled.button`
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--accent-dark);
  }
`;

const CancelEditButton = styled.button`
  background: #f1f5f9;
  color: #64748b;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }
`;

const NewRegistroSection = styled.div`
  position: relative;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
`;

const NewRegistroTextArea = styled.textarea`
  padding: 12px 16px;
  padding-bottom: 52px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1e293b;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: white;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const AddRegistroButton = styled.button`
  position: absolute;
  bottom: 30px;
  right: 10px;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(var(--accent-rgb), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DemandasContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DemandasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DemandaItem = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const DemandaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const DemandaTitle = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
`;

const DemandaStatus = styled.span<{ $status: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  
  ${props => {
    switch (props.$status) {
      case 'NAO_INICIADA':
        return `
          background: #dbeafe;
          color: #1e40af;
        `;
      case 'EM_ANDAMENTO':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      case 'PAUSADA':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      case 'CONCLUIDA':
        return `
          background: #d1fae5;
          color: #065f46;
        `;
      case 'CANCELADA':
        return `
          background: #fee2e2;
          color: #991b1b;
        `;
      default:
        return `
          background: #f1f5f9;
          color: #64748b;
        `;
    }
  }}
`;

const DemandaDescription = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
`;

const DemandaFooter = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const DemandaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #94a3b8;

  svg {
    flex-shrink: 0;
  }
`;

const PageHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MapFullScreen = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 56px);
  overflow: hidden;
  background: #ffffff;
`;

const MapTopLeft = styled.div`
  position: absolute;
  top: 32px;
  left: 40px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MapTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MapTitleIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #eff6ff;
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MapPageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.1;
`;

const MapPageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  margin: 4px 0 0 0;
`;

const MapTitleDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #e2e8f0;
  margin: 18px 0 12px;
`;

const MapSearchOverlay = styled.div`
  position: relative;
  width: 320px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const MapSearchClearButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(148, 163, 184, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
  
  svg {
    width: 14px;
    height: 14px;
    color: #64748b;
    transition: color 0.2s ease;
  }
  
  &:hover {
    background: rgba(148, 163, 184, 0.2);
    
    svg {
      color: #475569;
    }
  }
  
  &:active {
    transform: translateY(-50%) scale(0.9);
  }
`;

const MapSearchResults = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.10);
  overflow: hidden;
  z-index: 30;
`;

const MapSearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:not(:last-child) { border-bottom: 1px solid #f1f5f9; }
  &:hover { background: #f8fafc; }
`;

const MapResultAvatar = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const MapResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const MapResultName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MapResultCity = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
`;

const MapSearchNoResult = styled.div`
  padding: 14px 16px;
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
`;

const MapAddButton = styled.button`
  position: absolute;
  top: 32px;
  right: 40px;
  z-index: 20;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;

  svg { width: 20px; height: 20px; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.4);
  }
  &:active { transform: translateY(0); }
`;

const MapSearchInput = styled.input`
  width: 100%;
  padding: 16px 48px 16px 52px;
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 50px;
  font-size: 15px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(var(--accent-rgb), 0.3);
    box-shadow: 
      0 8px 30px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 1);
  }
  
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(var(--accent-rgb), 0.5);
    box-shadow: 
      0 12px 40px rgba(var(--accent-rgb), 0.15),
      0 4px 12px rgba(var(--accent-rgb), 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const MapLegendOverlay = styled.div`
  position: absolute;
  bottom: calc(80px + 72px + 12px);
  left: 16px;
  z-index: 20;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 18px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  min-width: 180px;
`;

const MapLegendTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
`;

const MapLegendGradient = styled.div`
  height: 8px;
  border-radius: 5px;
  background: linear-gradient(to right, var(--accent-dark), #06b6d4, #22c55e, #eab308, #f97316, #ef4444);
  margin-bottom: 5px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1e293b;
  font-size: 15px;
  font-weight: 500;
`;

const ToggleIcon = styled.div<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$active ? '#f59e0b' : '#94a3b8'};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToggleSwitch = styled.div<{ $active: boolean }>`
  position: relative;
  width: 48px;
  height: 26px;
  background: ${p => p.$active ? '#f59e0b' : '#cbd5e1'};
  border-radius: 13px;
  transition: background 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.$active ? '25px' : '3px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
`;

const MapLegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MapLegendLabel = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
`;

const MapStatsOverlay = styled.div<{ $isHidden?: boolean }>`
  position: absolute;
  bottom: 30px;
  left: 16px;
  z-index: 20;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 320px;
  opacity: ${p => p.$isHidden ? '0' : '1'};
  transform: translateX(${p => p.$isHidden ? '-10px' : '0'});
  pointer-events: ${p => p.$isHidden ? 'none' : 'auto'};
  transition: all 0.2s ease;
`;

const MapStatItem = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: 8px;
  transition: all 0.15s;
  background: ${p => p.$active ? '#eff6ff' : 'transparent'};
  &:hover { background: #f8fafc; }
`;

const MapStatIcon = styled.div<{ $color: string }>`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  background: ${p => p.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$color};
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MapStatContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
  flex: 1;
`;

const MapStatValue = styled.div<{ $active?: boolean }>`
  font-size: 24px;
  font-weight: 800;
  color: ${p => p.$active ? 'var(--accent)' : '#1e293b'};
  line-height: 1;
`;

const MapStatLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  line-height: 1;
`;

const MapStatDivider = styled.div`
  width: 1px;
  height: 36px;
  background: #e2e8f0;
  flex-shrink: 0;
`;

const shimmerAnimation = `
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const ShimmerBox = styled.div<{ width?: string; height?: string }>`
  ${shimmerAnimation}
  width: ${p => p.width || '100%'};
  height: ${p => p.height || '16px'};
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: shimmer 1.4s ease-in-out infinite;
`;

const ShimmerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
`;

// ── Mobile Contact Components ──

const MobileContactSearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 4px 12px 0;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }

  &:focus {
    outline: none;
  }
`;

const MobileContactList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
  }
`;

const MobileContactCard = styled.div<{ $birthday?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.15s;
  background: ${p => p.$birthday ? 'linear-gradient(to bottom, #fef6fb 0%, #ffffff 100%)' : 'white'};
  border-radius: 16px;
  border: 1px solid ${p => p.$birthday ? '#fbcfe8' : '#e8edf2'};
  box-shadow: 0 1px 5px rgba(0,0,0,0.04);

  &:active { background: ${p => p.$birthday ? '#fdf0f7' : '#f8fafc'}; transform: scale(0.99); }
`;

const MobileContactAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
`;

const MobileContactInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MobileContactName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
`;

const MobileContactSub = styled.div`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileContactRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const MobileBirthdayBadge = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const MobileContactChevron = styled.div`
  color: #cbd5e1;

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

const MobileContactShimmer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #e8edf2;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
`;

const MobileShimmerAvatarBox = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const MobileShimmerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const PhoneInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ── Mobile Filter Chips Row ──
const MobileFilterChipsRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    margin-bottom: 12px;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const MobileFilterChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  background: ${p => p.$active ? '#eff6ff' : 'white'};
  border: 1.5px solid ${p => p.$active ? '#bfdbfe' : '#e2e8f0'};
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$active ? '#1e40af' : '#475569'};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;

  &:active { opacity: 0.75; }
`;

const MobileFilterChipStar = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  background: ${p => p.$active ? '#fef3c7' : 'white'};
  border: 1.5px solid ${p => p.$active ? '#fcd34d' : '#e2e8f0'};
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$active ? '#92400e' : '#475569'};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;

  &:active { opacity: 0.75; }
`;

const PhoneActionButton = styled.button<{ $color: string }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: ${p => p.$color}15;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${p => p.$color};
    color: white;
  }

  &:active {
    transform: scale(0.93);
  }
`;

// ── Mobile Search Bar ──
const MobileSearchInputWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    position: relative;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.15s;

    &:focus-within {
      border-color: var(--accent);
    }
  }
`;

const MobileSearchIcon = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px 0 14px;
    color: #94a3b8;
    flex-shrink: 0;
    pointer-events: none;
  }
`;

const MobileSearchClear = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;

    &:active { color: #475569; }
  }
`;

const MobileFilterButton = styled.button<{ $active: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    border: 1.5px solid ${p => p.$active ? 'var(--accent)' : '#e2e8f0'};
    background: ${p => p.$active ? '#eff6ff' : 'white'};
    color: ${p => p.$active ? 'var(--accent)' : '#64748b'};
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.15s;

    &:active { transform: scale(0.93); }
  }
`;

const MobileFilterBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  border: 1.5px solid white;
`;

// ── Mobile Active Filter Chips ──
const MobileActiveFiltersRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    margin-bottom: 12px;
    padding-bottom: 2px;

    &::-webkit-scrollbar { display: none; }
  }
`;

const MobileActiveChip = styled.button<{ $color: string; $bg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  border: 1px solid ${p => p.$color}33;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;

  &:active { opacity: 0.75; }
`;

const MobileChipRemove = styled.span`
  font-size: 14px;
  font-weight: 400;
  opacity: 0.6;
  margin-left: 2px;
  line-height: 1;
`;

// ── Filter Bottom Sheet ──
const FilterSheetOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 3000;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  }
`;

const FilterSheet = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 88vh;
    background: white;
    border-radius: 20px 20px 0 0;
    z-index: 3001;
    animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);

    @keyframes slideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
  }
`;

const FilterSheetHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 12px auto 0;
  flex-shrink: 0;
`;

const FilterSheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
`;

const FilterSheetTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
`;

const FilterSheetClearAll = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #ef4444;
  cursor: pointer;
  padding: 4px 0;

  &:active { opacity: 0.7; }
`;

const FilterSheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 12px;
`;

const FilterSheetSection = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child { border-bottom: none; }
`;

const FilterSheetSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 10px;
`;

const FilterSheetRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  cursor: pointer;
  border-bottom: 1px solid #f8fafc;

  &:last-child { border-bottom: none; }
  &:active { opacity: 0.7; }
`;

const FilterSheetRowLabel = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
`;

const FilterSheetRowCheck = styled.div<{ $active: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${p => p.$active ? 'var(--accent)' : '#e2e8f0'};
  background: ${p => p.$active ? 'var(--accent)' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
`;

const FilterSheetSearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  box-sizing: border-box;
  margin-bottom: 8px;
  outline: none;

  &::placeholder { color: #94a3b8; }
  &:focus { border-color: var(--accent); background: white; }
`;

const FilterSheetOptionsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
`;

const FilterSheetOption = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: ${p => p.$active ? 600 : 500};
  color: ${p => p.$active ? 'var(--accent)' : '#1e293b'};
  background: ${p => p.$active ? '#eff6ff' : 'transparent'};
  cursor: pointer;
  border-bottom: 1px solid #f8fafc;
  transition: background 0.1s;

  &:last-child { border-bottom: none; }
  &:active { background: #f1f5f9; }
`;

const FilterSheetTagsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterSheetTagChip = styled.button<{ $active: boolean; $bg: string; $text: string; $border: string }>`
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.$active ? p.$text : p.$bg};
  color: ${p => p.$active ? 'white' : p.$text};
  border: 1.5px solid ${p => p.$active ? p.$text : p.$border};
  transition: all 0.15s;

  &:active { opacity: 0.75; }
`;

const FilterSheetApply = styled.button`
  margin: 0 20px 20px;
  padding: 16px;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  margin-bottom: calc(20px + env(safe-area-inset-bottom));

  &:active { transform: scale(0.98); }
`;

// ── Alpha Section Header ──
const AlphaSectionHeader = styled.div<{ $isBirthday?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 2px 4px;
  background: transparent;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${p => p.$isBirthday ? '#fbcfe8' : '#e2e8f0'};
  }
`;

const AlphaSectionLetter = styled.span<{ $isBirthday?: boolean }>`
  font-size: 11px;
  font-weight: 800;
  color: ${p => p.$isBirthday ? '#ec4899' : '#94a3b8'};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const AlphaSectionCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #b0bac7;
  white-space: nowrap;
  flex-shrink: 0;
`;

// ── Rich Mobile Card Components ──
const MobileCardAvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const MobileCardAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
`;

const MobileCardMultiBadge = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  font-size: 12px;
  line-height: 1;
  background: white;
  border-radius: 50%;
  padding: 1px;
`;

const MobileCardBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileCardNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const MobileCardName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileCardBirthdayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  background: #fdf2f8;
  color: #ec4899;
  border: 1px solid #fbcfe8;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

const MobileCardMenuBtn = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  color: #cbd5e1;
  padding: 2px 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:active { background: #f1f5f9; color: #64748b; }
`;

const MobileCardSubRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileCardTagsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const MobileCardTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
`;

const MobileCardPhoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px dashed #e2e8f0;
`;

const MobileCardPhone = styled.span`
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const MobileCardActionBtn = styled.button<{ $color: string }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  background: ${p => p.$color}18;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;

  &:active {
    background: ${p => p.$color};
    color: white;
    transform: scale(0.9);
  }
`;

// ── Inline-edit infrastructure (contact modal) ──

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinnerCircle = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const HoverShell = styled.div<{ $canEdit: boolean; $block?: boolean }>`
  display: ${p => p.$block ? 'block' : 'inline-flex'};
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 6px;
  cursor: ${p => p.$canEdit ? 'pointer' : 'default'};
  transition: background-color 0.15s ease;
  min-height: 24px;
  ${p => p.$block ? 'flex: 1; min-width: 0;' : ''}

  &:hover {
    background: ${p => p.$canEdit ? 'rgba(15, 23, 42, 0.05)' : 'transparent'};
  }
`;

const PropertiesSection = styled.div`
  margin-bottom: 20px;
`;

const PropertiesLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PropertyRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  overflow: visible;

  @media (max-width: 768px) {
    grid-template-columns: 110px 1fr;
    min-height: 34px;
  }
`;

const PropertyLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;

  svg {
    width: 13px;
    height: 13px;
    color: #9ca3af;
    flex-shrink: 0;
  }
`;

const PropertyValue = styled.div`
  font-size: 13px;
  color: #111827;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const IEInput = styled.input`
  width: 100%;
  padding: 3px 7px;
  border: 1.5px solid var(--accent);
  border-radius: 4px;
  font-size: 13px;
  color: #111827;
  line-height: 1.4;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const IETextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--accent);
  border-radius: 6px;
  font-size: 13px;
  color: #111827;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ContactDescription = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const IECustomSelectWrapper = styled.div`
  position: relative;
  min-width: 0;
  width: 100%;
`;

const IECustomSelectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 3px 7px;
  background: white;
  border: 1.5px solid var(--accent);
  border-radius: 4px;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: #6b7280;
  }

  &:focus {
    outline: none;
  }
`;

const IECustomSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  min-width: 180px;
  width: max-content;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  overflow: hidden;
`;

const IEDropdownSearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #1e293b;
  box-sizing: border-box;
  outline: none;

  &::placeholder { color: #94a3b8; }
  &:focus { border-bottom-color: var(--accent); }
`;

const IECustomSelectOptionsContainer = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const IECustomSelectOption = styled.div`
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  transition: background 0.1s;

  &:hover { background: #f8fafc; }
`;

const MultiplicadorBadge = styled.span<{ $active: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.$active ? '#fef3c7' : '#f1f5f9'};
  color: ${p => p.$active ? '#92400e' : '#64748b'};
`;

const InlinePhoneButton = styled.button<{ $color: string }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: ${p => p.$color}18;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    background: ${p => p.$color};
    color: white;
  }
`;

// ── Inline-edit mini-components ──

interface IETextProps {
  value: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (value: string) => void | Promise<void>;
  placeholder?: string;
  emptyLabel?: string;
  renderDisplay?: (value: string) => React.ReactNode;
  allowEmpty?: boolean;
}

const InlineEditText: React.FC<IETextProps> = ({
  value, canEdit, saving, onSave, placeholder, emptyLabel = '-',
  renderDisplay, allowEmpty = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => { if (editing) { setDraft(value); committedRef.current = false; } }, [editing, value]);
  useEffect(() => { if (!saving) setPending(false); }, [saving]);

  useLayoutEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
    }
  }, [editing]);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    const next = draft.trim();
    if (!allowEmpty && !next) return;
    if (next !== (value ?? '')) { setPending(true); onSave(next); }
  };

  if (editing) {
    return (
      <IEInput
        ref={inputRef}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }

  const displayValue = value || emptyLabel;
  return (
    <HoverShell $canEdit={canEdit} onClick={() => canEdit && setEditing(true)}>
      {(saving || pending) ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(displayValue) : displayValue)}
    </HoverShell>
  );
};

interface IETextAreaProps {
  value: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (value: string) => void | Promise<void>;
  placeholder?: string;
  emptyLabel?: string;
}

const InlineEditTextArea: React.FC<IETextAreaProps> = ({
  value, canEdit, saving, onSave, placeholder, emptyLabel = '-',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (!saving) setPending(false); }, [saving]);

  useLayoutEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.setSelectionRange(taRef.current.value.length, taRef.current.value.length);
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value ?? '')) { setPending(true); onSave(draft); }
  };

  if (editing) {
    return (
      <IETextArea
        ref={taRef}
        value={draft}
        rows={Math.max(4, draft.split('\n').length + 1)}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }

  return (
    <HoverShell $canEdit={canEdit} $block onClick={() => canEdit && setEditing(true)}>
      {(saving || pending) ? <SpinnerCircle /> : <ContactDescription>{value || emptyLabel}</ContactDescription>}
    </HoverShell>
  );
};

interface IESelectOption { value: string; label: string }

interface IESelectProps {
  value: string | undefined;
  canEdit: boolean;
  saving: boolean;
  options: IESelectOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  onSave: (value: string) => void | Promise<void>;
  renderDisplay?: (label: string) => React.ReactNode;
}

const InlineEditSelect: React.FC<IESelectProps> = ({
  value, canEdit, saving, options, searchable, searchPlaceholder,
  emptyLabel = '-', onSave, renderDisplay,
}) => {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setEditing(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [editing]);

  const currentLabel = value ? (options.find(o => o.value === value)?.label ?? value) : '';

  if (!editing) {
    const display = currentLabel || emptyLabel;
    return (
      <HoverShell $canEdit={canEdit} onClick={() => canEdit && setEditing(true)}>
        {saving ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(display) : display)}
      </HoverShell>
    );
  }

  const filtered = searchable
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 50)
    : options;

  return (
    <IECustomSelectWrapper ref={wrapperRef}>
      <IECustomSelectButton onClick={(e) => e.stopPropagation()}>
        {currentLabel || 'Selecione'}
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </IECustomSelectButton>
      <IECustomSelectDropdown>
        {searchable && (
          <IEDropdownSearchInput
            type="text"
            placeholder={searchPlaceholder ?? 'Buscar...'}
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <IECustomSelectOptionsContainer>
          {filtered.map((opt) => (
            <IECustomSelectOption
              key={opt.value}
              onClick={() => {
                setEditing(false);
                setSearch('');
                if (opt.value !== value) onSave(opt.value);
              }}
            >
              {opt.label}
            </IECustomSelectOption>
          ))}
        </IECustomSelectOptionsContainer>
      </IECustomSelectDropdown>
    </IECustomSelectWrapper>
  );
};

interface IEDateProps {
  value: string | undefined;
  canEdit: boolean;
  saving: boolean;
  onSave: (value: string | null) => void | Promise<void>;
  emptyLabel?: string;
  renderDisplay?: (formatted: string) => React.ReactNode;
}

const InlineEditDate: React.FC<IEDateProps> = ({
  value, canEdit, saving, onSave, emptyLabel = '-', renderDisplay,
}) => {
  const [editing, setEditing] = useState(false);
  const isoValue = value ? value.slice(0, 10) : '';
  const [draft, setDraft] = useState(isoValue);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => { if (editing) { setDraft(isoValue); committedRef.current = false; } }, [editing, isoValue]);
  useEffect(() => { if (!saving) setPending(false); }, [saving]);

  useLayoutEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    if (draft !== isoValue) { setPending(true); onSave(draft || null); }
  };

  if (editing) {
    return (
      <IEInput
        ref={inputRef}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(isoValue); setEditing(false); }
        }}
      />
    );
  }

  const formatted = value ? (() => {
    const parts = value.slice(0, 10).split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])).toLocaleDateString('pt-BR');
    }
    return value;
  })() : emptyLabel;

  return (
    <HoverShell $canEdit={canEdit} onClick={() => canEdit && setEditing(true)}>
      {(saving || pending) ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(formatted) : formatted)}
    </HoverShell>
  );
};

// ── Birthday Banner Send Button ──
const BirthdayBannerSendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 12px;
  background: white;
  color: #ea580c;
  border: 1.5px solid #fed7aa;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;

  &:active {
    background: #fff7ed;
    transform: scale(0.96);
  }
`;

// ── Contact Detail Modal (2-column full layout) ──

const ContactDetailModal = styled.div`
  position: relative;
  background: #fff;
  border-radius: 16px;
  width: 92vw;
  max-width: 1200px;
  height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,0.22);
`;

const CDHeader = styled.div`
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 18px 24px 14px;
  flex-shrink: 0;
`;

const CDHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CDHeaderBottom = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const CDMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const CDMetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #f3f4f6;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CDMetaDot = styled.span`
  color: #d1d5db;
  font-size: 13px;
  line-height: 1;
`;

const CDMetaText = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const CDHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CDNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CDName = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  display: flex;
  align-items: center;

  /* make the inline-edit input match the heading style */
  input {
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
    padding: 2px 6px;
    border-radius: 6px;
    min-width: 200px;
  }
`;

const CDMultiplicadorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  background: #fef3c7;
  color: #d97706;
  border: 1px solid #fde68a;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CDActionBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CDActionBtn = styled.button<{ $primary?: boolean; $call?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s;
  white-space: nowrap;
  border: none;
  background: ${({ $primary, $call }) => $primary ? '#e8f5ee' : $call ? '#e8eefa' : '#f1f5f9'};
  color: ${({ $primary, $call }) => $primary ? '#1a9c4e' : $call ? '#3b6fe0' : '#374151'};
  &:hover { filter: brightness(0.96); }
  &:active { filter: brightness(0.92); }
  svg { width: 16px; height: 16px; flex-shrink: 0; }
`;

const CDIconBtn = styled.button`
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover { background: #f3f4f6; color: #374151; }
  &.danger:hover { background: #fef2f2; color: #ef4444; border-color: #fecaca; }
  svg { width: 16px; height: 16px; }
`;

const CDBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const CDLeft = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CDRight = styled.div`
  width: 380px;
  min-width: 380px;
  flex-shrink: 0;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  background: #fff;
  min-height: 0;
`;

const CDSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CDSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-bottom: 10px;
`;

const CDInfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const CDInfoRow = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  align-items: center;
  gap: 8px;
  height: 34px;
  overflow: visible;
`;

const CDInfoLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  svg { width: 14px; height: 14px; color: #9ca3af; flex-shrink: 0; }
`;

const CDInfoValue = styled.div`
  font-size: 14px;
  color: #111827;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const CDTodayBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #dcfce7;
  color: #16a34a;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 6px;
`;

const CDRightTabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
  flex-shrink: 0;
  padding: 0 20px;
`;

const CDRightTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 14px 4px 12px;
  margin-right: 20px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.15s;
  border-bottom: 2px solid ${({ $active }) => $active ? '#6366f1' : 'transparent'};
  color: ${({ $active }) => $active ? '#6366f1' : '#6b7280'};
  white-space: nowrap;
  &:hover { color: #374151; }
  svg { flex-shrink: 0; }
`;

const CDRightTabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #e5e7eb;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
`;

const CDRightFilterRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
`;

const CDFilterChip = styled.button<{ $active?: boolean }>`
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${({ $active }) => $active ? 'var(--accent, #3b82f6)' : '#e5e7eb'};
  background: ${({ $active }) => $active ? 'var(--accent, #3b82f6)' : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#6b7280'};
  transition: all 0.15s;
`;

const CDRightBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 20px 20px;
  display: flex;
  flex-direction: column;
`;

const CDRightFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 16px 16px;
  background: #fff;
`;

const CDInputBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const CDInputArea = styled.textarea`
  width: 100%;
  border: none;
  padding: 12px 14px 6px;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  outline: none;
  color: #374151;
  background: transparent;
  box-sizing: border-box;
  display: block;
  &::placeholder { color: #9ca3af; }
`;

const CDInputFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 6px 10px;
  border-top: 1px solid #f3f4f6;
`;

const CDSendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  border: none;
  background: #6366f1;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const CDRegistroItem = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CDRegistroMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CDRegistroAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CDRegistroAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--accent, #3b82f6);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CDRegistroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CDRegistroTime = styled.span`
  font-size: 10px;
  color: #9ca3af;
`;

const CDRegistroMenuWrap = styled.div`
  position: relative;
`;

const CDRegistroMenuBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #f3f4f6; color: #374151; }
  svg { width: 14px; height: 14px; }
`;

const CDRegistroText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CDEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: #9ca3af;
  text-align: center;
  svg { width: 32px; height: 32px; opacity: 0.4; }
  span { font-size: 13px; }
`;

// ── Feed / timeline components ──

const CDFilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 12px;
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
`;

const CDFilterCount = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CDFilterChips = styled.div`
  display: flex;
  gap: 2px;
`;

/* Filter chips — all use same RegistrosFilterTab style from DemandDetailModal */
const CDFilterChipBox = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'white' : 'transparent'};
  border: ${({ $active }) => $active ? '1px solid #e5e7eb' : '1px solid transparent'};
  border-radius: 7px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ $active }) => $active ? '#111827' : '#6b7280'};
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'};
  &:hover { color: #111827; }
`;

/* Same pill style for Comentários / Histórico */
const CDFilterChipText = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'white' : 'transparent'};
  border: ${({ $active }) => $active ? '1px solid #e5e7eb' : '1px solid transparent'};
  border-radius: 7px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ $active }) => $active ? '#111827' : '#6b7280'};
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'};
  &:hover { color: #111827; }
`;

/* Date separator — plain left-aligned grey label */
const CDDateGroup = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 12px 0 6px;
`;

/* Outer timeline wrapper */
const CDTimeline = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Each entry: left column (avatar + line) + right column (content) */
const CDTimelineItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

/* Left: avatar stacked above the connecting line */
const CDTimelineLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
`;

/* Thin vertical line connecting to next item */
const CDTimelineLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 16px;
  background: #e9edf2;
  margin: 4px 0;
`;

/* Right side: header row + optional card */
const CDTimelineContent = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: 12px;
  padding-top: 5px;
`;

/* Icon-based avatar circle — warm yellow/clock for history, blue/doc for comments */
const CDIconAvatar = styled.div<{ $type: 'comment' | 'history' }>`
  width: 34px;
  height: 34px;
  min-height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $type }) => $type === 'comment' ? '#e8eaf6' : '#f0fdf4'};
  color: ${({ $type }) => $type === 'comment' ? '#5c6bc0' : '#16a34a'};
  border: none;
`;

const CDFeedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
  line-height: 1.45;
  font-size: 13px;
  min-height: 34px;
`;

const CDFeedMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;
  font-size: 13px;
`;

const CDFeedAuthor = styled.span`
  font-weight: 700;
  color: #111827;
  margin-right: 2px;
`;

const CDFeedAction = styled.span`
  color: #6b7280;
  font-size: 13px;
`;

const CDFeedTime = styled.span`
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 4px;
`;

const CDFeedCard = styled.div`
  margin-top: 6px;
  background: white;
  border: 1px solid #e9edf2;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const CDCreatorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const CDCreatorPill = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px 8px 8px;
  background: #f8fafc;
  border: 1px solid #e9edf2;
  border-radius: 999px;
`;

const CDCreatorAvatar = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const CDCreatorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CDCreatorLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 1px;
`;

const CDCreatorName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CDCreatorDate = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
`;

const CDDeleteContactBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: none;
  background: transparent;
  color: #ef4444;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { background: #fef2f2; }
`;

const CDFooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 6px 10px 8px;
  gap: 8px;
`;

const CDFooterIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const CDFooterIcon = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover { background: #f3f4f6; color: #6b7280; }
`;

const CDFooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CDFooterHint = styled.span`
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
`;

const CDInfoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;
